import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { Badge, Button, Col, Row } from "reactstrap";
import {
  Card,
  Table,
  Tag,
  Descriptions,
  Divider,
  Space,
  message,
  Tooltip,
  Popover,
  Button as AntdButton,
  Timeline,
} from "antd";
import {
  CheckCircleOutlined,
  FileTextOutlined,
  DollarOutlined,
  PaperClipOutlined,
  DownloadOutlined,
  DownOutlined,
  RightOutlined,
  InfoCircleOutlined,
  ScheduleOutlined,
  EyeOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  WorkOrderFormData,
  WorkOrderLine,
  WorkOrderLineSource,
  TaxApplicationLevel,
  WorkType,
} from "../types";
import workOrderApis from "../../../api/WorkOrderManagement/WorkOrderApis";
import dropdownApis from "../../../api/WorkOrderManagement/DropdownApis";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";

const WorkOrderPreview: React.FC = (props: any) => {
  const { handleSubmit, getValues, watch } = useFormContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";
  const [submitting, setSubmitting] = useState(false);
  const [taxOptions, setTaxOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  // Get current user state for GST calculation
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const [currentUserState, setCurrentUserState] = useState<string>("");
  const [contractorState, setContractorState] = useState<string>("");

  // Get original data from main component (captured at load time)
  const originalData = props.originalEditData;
  const [deletedItemsTracker, setDeletedItemsTracker] = useState<{
    workOrderLineIds: number[];
    generalConditionIds: number[];
    paymentConditionIds: number[];
  }>({
    workOrderLineIds: [],
    generalConditionIds: [],
    paymentConditionIds: [],
  });

  const formData: any = getValues();
  const workOrderLinesWatch = watch("workOrderLines") || [];
  const generalConditionsWatch = watch("generalConditions") || [];
  const paymentConditionsWatch = watch("paymentConditions");

  // Fetch tax options for ID to label mapping
  const fetchTaxOptions = async () => {
    try {
      const response = await dropdownApis.fetchTaxDropdown();
      if (response?.data && Array.isArray(response.data)) {
        const taxOpts = response.data.map((tax: any) => ({
          label: `${tax.taxName || tax.name} - ${tax.taxRate || tax.rate || 0
            }%`,
          value: tax.taxId?.toString() || tax.id?.toString(),
        }));
        setTaxOptions(taxOpts);
        console.log("Tax options loaded for preview:", taxOpts);
      }
    } catch (error) {
      console.error("Error fetching tax options in preview:", error);
    }
  };

  // Set current user state
  useEffect(() => {
    if (currentUser?.registeredAddress?.state?.stateId) {
      setCurrentUserState(
        currentUser.registeredAddress.state.stateId.toString()
      );
      console.log(
        "Current user state ID for GST:",
        currentUser.registeredAddress.state.stateId
      );
    }
  }, [currentUser]);

  // Set contractor state from form data
  useEffect(() => {
    const getContractorState = async () => {
      const formData = getValues();
      if (formData.contractorId && formData.siteId) {
        try {
          // Get contractor state from existing contractors API
          const response = await dropdownApis.fetchContractorsBySiteIdDropdown({
            id: formData.siteId.toString(),
          });

          if (response?.status && response?.data) {
            const contractor = response.data.find((c: any) => {
              const contractorId = (
                c.contractorID ||
                c.contractorId ||
                c.id
              )?.toString();
              return contractorId === formData.contractorId;
            });

            if (contractor) {
              const stateId = contractor.state;

              if (stateId) {
                setContractorState(stateId.toString());
                console.log("Contractor state ID for GST:", stateId);
              }
            }
          }
        } catch (error) {
          console.warn(
            "Could not fetch contractor state for GST calculation:",
            error
          );
        }
      }
    };

    getContractorState();
  }, []);

  // Scroll to top when component mounts (when navigating from Payment Conditions)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchTaxOptions();

    // Debug: Show original data received from main component
    if (originalData) {
      console.log("=== ORIGINAL DATA RECEIVED FROM MAIN COMPONENT ===");
      console.log("Original Work Order Lines:", originalData.workOrderLines);
      console.log(
        "Original General Conditions:",
        originalData.generalConditions
      );
    }
  }, []);

  // Track deletions in work order lines
  useEffect(() => {
    console.log("=== WORK ORDER LINES DELETION TRACKING ===");
    console.log("Original Data exists:", !!originalData);
    console.log("Form type:", formData.type);
    console.log("Current Work Order Lines Watch:", workOrderLinesWatch);

    if (
      originalData &&
      originalData.workOrderLines &&
      formData.type === "edit"
    ) {
      console.log(
        "Original Work Order Lines from main component:",
        originalData.workOrderLines
      );

      const currentLinesWithIds = workOrderLinesWatch.filter(
        (line: any) => line.workOrderLineId
      );
      console.log("Current lines with workOrderLineId:", currentLinesWithIds);

      const currentDbIds = currentLinesWithIds.map((line: any) =>
        Number(line.workOrderLineId)
      );
      console.log("Current DB IDs:", currentDbIds);

      const originalLinesWithIds = originalData.workOrderLines.filter(
        (line: any) => line.workOrderLineId
      );
      console.log("Original lines with workOrderLineId:", originalLinesWithIds);

      const originalDbIds = originalLinesWithIds.map((line: any) =>
        Number(line.workOrderLineId)
      );
      console.log("Original DB IDs:", originalDbIds);
      console.log("Looking for deleted ID 45 specifically...");
      console.log("Is 45 in original?", originalDbIds.includes(45));
      console.log("Is 45 in current?", currentDbIds.includes(45));

      const newlyDeletedIds = originalDbIds.filter(
        (id: any) => !currentDbIds.includes(id)
      );
      console.log("Newly deleted IDs:", newlyDeletedIds);

      if (newlyDeletedIds.length > 0) {
        console.log("SETTING DELETED WORK ORDER LINE IDS:", newlyDeletedIds);
        setDeletedItemsTracker((prev: any) => {
          const uniqueIds = Array.from(
            new Set([...prev.workOrderLineIds, ...newlyDeletedIds])
          );
          console.log("Updated tracker with work order line IDs:", uniqueIds);
          return {
            ...prev,
            workOrderLineIds: uniqueIds,
          };
        });
      }
    }
  }, [workOrderLinesWatch, originalData]);

  // Track deletions in general conditions
  useEffect(() => {
    console.log("=== GENERAL CONDITIONS DELETION TRACKING ===");
    console.log("Original Data exists:", !!originalData);
    console.log("Current General Conditions Watch:", generalConditionsWatch);

    if (
      originalData &&
      originalData.generalConditions &&
      formData.type === "edit"
    ) {
      console.log(
        "Original General Conditions from main component:",
        originalData.generalConditions
      );

      const currentDbIds = generalConditionsWatch
        .filter((condition: any) => condition.workOrderConditionId)
        .map((condition: any) => Number(condition.workOrderConditionId));
      console.log("Current General Condition DB IDs:", currentDbIds);

      const originalDbIds = originalData.generalConditions
        .filter((condition: any) => condition.workOrderConditionId)
        .map((condition: any) => Number(condition.workOrderConditionId));
      console.log("Original General Condition DB IDs:", originalDbIds);

      const newlyDeletedIds = originalDbIds.filter(
        (id: any) => !currentDbIds.includes(id)
      );
      console.log("Newly deleted General Condition IDs:", newlyDeletedIds);

      if (newlyDeletedIds.length > 0) {
        console.log("SETTING DELETED GENERAL CONDITION IDS:", newlyDeletedIds);
        setDeletedItemsTracker((prev: any) => {
          const uniqueIds = Array.from(
            new Set([...prev.generalConditionIds, ...newlyDeletedIds])
          );
          console.log("Updated tracker with general condition IDs:", uniqueIds);
          return {
            ...prev,
            generalConditionIds: uniqueIds,
          };
        });
      }
    }
  }, [generalConditionsWatch, originalData]);

  const handlePrevious = () => {
    if (props.prev) {
      props.prev();
    }
  };

  const validateFinalSubmission = (data: WorkOrderFormData) => {
    if (workOrderLinesWatch.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please add at least one work order line",
      });
      return false;
    }

    if (!data.workOrderTitle?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Order Title is required",
      });
      return false;
    }

    if (!data.siteId) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Site selection is required",
      });
      return false;
    }

    return true;
  };

  // Helper functions for change tracking
  const getChangedFields = (original: any, current: any) => {
    const changes: any = {};

    // Compare basic fields
    const fieldsToCompare = [
      "linkedWorkRequest",
      "linkedTender",
      "contractDate",
      "workOrderTitle",
      "workOrderDescription",
      "siteId",
      "siteName",
      "projectId",
      "projectName",
      "contractType",
      "contractWith",
      "contractorId",
      "contractorName",
      "workOrderOwner",
      "workOrderOwnerName",
      "plannedStartDate",
      "plannedEndDate",
      "approvalRequired",
      "isStrictApproval",
      "enableTax",
      "taxApplicationLevel",
      "headerTaxType",
      "tdsTaxType",
      "tcsTaxType",
    ];

    fieldsToCompare.forEach((field) => {
      let originalValue = original[field];
      let currentValue = current[field];

      // Handle dayjs date objects - convert to string for comparison
      if (
        originalValue &&
        typeof originalValue === "object" &&
        originalValue.format
      ) {
        originalValue = originalValue.format("YYYY-MM-DD");
      }
      if (
        currentValue &&
        typeof currentValue === "object" &&
        currentValue.format
      ) {
        currentValue = currentValue.format("YYYY-MM-DD");
      }

      // Compare values, handling null/undefined/empty string equivalence
      const normalizeValue = (val: any) => {
        if (val === null || val === undefined || val === "") return "";
        return String(val);
      };

      if (normalizeValue(originalValue) !== normalizeValue(currentValue)) {
        console.log(
          `Field ${field} changed: '${originalValue}' -> '${currentValue}'`
        );
        changes[field] = current[field];
      }
    });

    return changes;
  };

  const getDeletedWorkOrderLines = (
    originalLines: any[],
    currentLines: any[]
  ) => {
    // Get original line IDs that are numeric (from database)
    const originalDbIds = originalLines
      .filter(
        (line) =>
          line.workOrderLineId ||
          (line.id && !line.id.toString().startsWith("wol-"))
      )
      .map((line) => line.workOrderLineId || line.id);

    // Get current line IDs that are numeric (from database)
    const currentDbIds = currentLines
      .filter(
        (line) =>
          line.workOrderLineId ||
          (line.id && !line.id.toString().startsWith("wol-"))
      )
      .map((line) => line.workOrderLineId || line.id);

    // Return deleted database IDs
    return originalDbIds.filter((id) => !currentDbIds.includes(id));
  };

  const getDeletedGeneralConditions = (
    originalConditions: any[],
    currentConditions: any[]
  ) => {
    // Get original condition database IDs
    const originalDbIds = originalConditions
      .filter((condition) => condition.workOrderConditionId)
      .map((condition) => condition.workOrderConditionId);

    // Get current condition database IDs (conditions that have database IDs)
    const currentDbIds = currentConditions
      .filter((condition) => condition.workOrderConditionId)
      .map((condition) => condition.workOrderConditionId);

    // Return deleted database IDs
    return originalDbIds.filter((id) => !currentDbIds.includes(id));
  };

  const getDeletedPaymentConditions = (
    originalPayment: any,
    currentPayment: any
  ) => {
    // For payment conditions, we need to track which ones were removed
    // This is more complex as payment conditions are stored as an object, not array
    const deletedIds: number[] = [];

    // TODO: Implement payment condition deletion tracking based on your data structure
    // This would require storing original payment condition IDs

    return deletedIds;
  };

  const hasWorkOrderLinesChanged = (
    originalLines: any[],
    currentLines: any[]
  ) => {
    // Don't consider it "changed" just because lengths are different - that's deletions/additions
    // Only consider it changed if existing items have been modified

    // Check if any existing lines (matched by workOrderLineId) have been modified
    const currentLinesWithIds = currentLines.filter(
      (line) => line.workOrderLineId
    );
    const originalLinesWithIds = originalLines.filter(
      (line) => line.workOrderLineId
    );

    // Check for modifications to existing lines
    const hasModifiedExistingLines = currentLinesWithIds.some((currentLine) => {
      const originalLine = originalLinesWithIds.find(
        (orig) => orig.workOrderLineId === currentLine.workOrderLineId
      );

      if (!originalLine) return false; // This is a new line, not a modification

      return (
        originalLine.taskTitle !== currentLine.taskTitle ||
        originalLine.taskDescription !== currentLine.taskDescription ||
        originalLine.quantity !== currentLine.quantity ||
        originalLine.rate !== currentLine.rate ||
        originalLine.weightagePercentage !== currentLine.weightagePercentage ||
        originalLine.workCategory !== currentLine.workCategory ||
        originalLine.uom !== currentLine.uom ||
        originalLine.taxType !== currentLine.taxType ||
        originalLine.rate !== currentLine.rate ||
        originalLine.towerVilla !== currentLine.towerVilla ||
        originalLine.floorName !== currentLine.floorName ||
        originalLine.flatName !== currentLine.flatName
      );
    });

    // Check for new lines (lines without workOrderLineId)
    const hasNewLines = currentLines.some((line) => !line.workOrderLineId);

    console.log("Work Order Lines Change Detection:");
    console.log("- Has modified existing lines:", hasModifiedExistingLines);
    console.log("- Has new lines:", hasNewLines);

    return hasModifiedExistingLines || hasNewLines;
  };

  // Get only changed/new work order lines
  const getChangedWorkOrderLines = (
    originalLines: any[],
    currentLines: any[]
  ) => {
    const changedLines: any[] = [];

    // Add new lines (lines without workOrderLineId)
    const newLines = currentLines.filter((line) => !line.workOrderLineId);
    changedLines.push(...newLines);
    console.log("changedLines:", changedLines);
    // Add modified existing lines
    const currentLinesWithIds = currentLines.filter(
      (line) => line.workOrderLineId
    );
    const originalLinesWithIds = originalLines.filter(
      (line) => line.workOrderLineId
    );

    currentLinesWithIds.forEach((currentLine) => {
      const originalLine = originalLinesWithIds.find(
        (orig) => orig.workOrderLineId === currentLine.workOrderLineId
      );

      if (originalLine) {
        const hasChanged =
          originalLine.taskTitle !== currentLine.taskTitle ||
          originalLine.taskDescription !== currentLine.taskDescription ||
          originalLine.quantity !== currentLine.quantity ||
          originalLine.rate !== currentLine.rate ||
          originalLine.weightagePercentage !==
          currentLine.weightagePercentage ||
          originalLine.workCategory !== currentLine.workCategory ||
          originalLine.uom !== currentLine.uom ||
          originalLine.taxType !== currentLine.taxType ||
          originalLine.towerVilla !== currentLine.towerVilla ||
          originalLine.floorName !== currentLine.floorName ||
          originalLine.flatName !== currentLine.flatName ||
          originalLine.scheduledStartDate !== currentLine.scheduledStartDate ||
          originalLine.scheduledEndDate !== currentLine.scheduledEndDate;

        if (hasChanged) {
          changedLines.push(currentLine);
        }
      }
    });

    console.log("Changed/New Lines:", changedLines);
    return changedLines;
  };

  const hasGeneralConditionsChanged = (
    originalConditions: any[],
    currentConditions: any[]
  ) => {
    // Don't consider it "changed" just because lengths are different - that's deletions/additions
    // Only consider it changed if existing items have been modified or new ones added

    // Check for modifications to existing conditions
    const hasModifiedExistingConditions = currentConditions.some(
      (currentCondition) => {
        const originalCondition = originalConditions.find(
          (orig) => orig.id === currentCondition.id
        );

        if (!originalCondition) {
          // This is a new condition (not in original)
          return true;
        }

        // Check if content has been modified
        return (
          originalCondition.conditionContent !==
          currentCondition.conditionContent
        );
      }
    );

    console.log("General Conditions Change Detection:");
    console.log(
      "- Has modified/new conditions:",
      hasModifiedExistingConditions
    );

    return hasModifiedExistingConditions;
  };

  const hasPaymentConditionsChanged = (
    originalPayment: any,
    currentPayment: any
  ) => {
    console.log("=== PAYMENT CONDITIONS CHANGE CHECK ===");
    console.log("Original Payment Object:", originalPayment);
    console.log("Current Payment Object:", currentPayment);

    if (!originalPayment || !currentPayment) {
      console.log("Missing payment data, returning false");
      return false;
    }

    // Normalize values for comparison (convert null/undefined to defaults)
    const normalizePaymentCondition = (condition: any, type: string) => {
      if (!condition) return {};

      const normalized = { ...condition };

      // Convert null/undefined to false/0 for consistent comparison
      if (type === "mobilizationAdvance") {
        normalized.isPercentOfContract =
          normalized.isPercentOfContract || false;
        normalized.percentageOfContract = normalized.percentageOfContract || 0;
        normalized.advanceValue = normalized.advanceValue || 0;
        normalized.interestCharged = normalized.interestCharged || false;
        normalized.interestRate = normalized.interestRate || 0;
        normalized.recoveryType = normalized.recoveryType || "Each RA Bill";
      } else if (type === "retentionAmount") {
        normalized.isPercentOfRABill = normalized.isPercentOfRABill || false;
        normalized.percentageOfRABill = normalized.percentageOfRABill || 0;
        normalized.retentionValue = normalized.retentionValue || 0;
        normalized.dlpAmount = normalized.dlpAmount || 1;
        normalized.dlpUnit = normalized.dlpUnit || "years";
        normalized.releaseDate = normalized.releaseDate || null;
      } else if (type === "onHoldAmount") {
        normalized.isPercentOfContract =
          normalized.isPercentOfContract || false;
        normalized.percentageOfContract = normalized.percentageOfContract || 0;
        normalized.holdAmountValue = normalized.holdAmountValue || 0;
        normalized.dlpAmount = normalized.dlpAmount || 1;
        normalized.dlpUnit = normalized.dlpUnit || "years";
        normalized.releaseDate = normalized.releaseDate || null;
      }

      return normalized;
    };

    // Normalize both original and current conditions
    const originalNormalized = {
      mobilizationAdvance: normalizePaymentCondition(
        originalPayment.mobilizationAdvance,
        "mobilizationAdvance"
      ),
      retentionAmount: normalizePaymentCondition(
        originalPayment.retentionAmount,
        "retentionAmount"
      ),
      onHoldAmount: normalizePaymentCondition(
        originalPayment.onHoldAmount,
        "onHoldAmount"
      ),
      payOnWorkCompletion: originalPayment.payOnWorkCompletion || false,
    };

    const currentNormalized = {
      mobilizationAdvance: normalizePaymentCondition(
        currentPayment.mobilizationAdvance,
        "mobilizationAdvance"
      ),
      retentionAmount: normalizePaymentCondition(
        currentPayment.retentionAmount,
        "retentionAmount"
      ),
      onHoldAmount: normalizePaymentCondition(
        currentPayment.onHoldAmount,
        "onHoldAmount"
      ),
      payOnWorkCompletion: currentPayment.payOnWorkCompletion || false,
    };

    // Check each condition individually with normalized comparison
    const mobilizationChanged =
      JSON.stringify(originalNormalized.mobilizationAdvance) !==
      JSON.stringify(currentNormalized.mobilizationAdvance);
    const retentionChanged =
      JSON.stringify(originalNormalized.retentionAmount) !==
      JSON.stringify(currentNormalized.retentionAmount);
    const onHoldChanged =
      JSON.stringify(originalNormalized.onHoldAmount) !==
      JSON.stringify(currentNormalized.onHoldAmount);
    const payOnCompletionChanged =
      originalNormalized.payOnWorkCompletion !==
      currentNormalized.payOnWorkCompletion;

    console.log("=== INDIVIDUAL CONDITION COMPARISONS (NORMALIZED) ===");
    console.log("Mobilization Changed:", mobilizationChanged);
    console.log("Retention Changed:", retentionChanged);
    console.log("On Hold Changed:", onHoldChanged);
    console.log("Pay On Completion Changed:", payOnCompletionChanged);

    // Deep comparison of payment condition values
    const hasChanges =
      mobilizationChanged ||
      retentionChanged ||
      onHoldChanged ||
      payOnCompletionChanged;

    console.log("=== FINAL RESULT ===");
    console.log("Has Payment Conditions Changed:", hasChanges);

    return hasChanges;
  };

  const getChangedPaymentConditions = (
    originalPayment: any,
    currentPayment: any,
    transformedPaymentConditions: any[]
  ) => {
    console.log("=== PAYMENT CONDITIONS CHANGE DETECTION DEBUG ===");
    console.log("Original Payment:", originalPayment);
    console.log("Current Payment:", currentPayment);

    if (!originalPayment || !currentPayment) {
      console.log("Missing payment data, returning all transformed conditions");
      return transformedPaymentConditions;
    }

    const changedConditions: any[] = [];

    // Check each condition type individually
    const conditionTypes = [
      "mobilizationAdvance",
      "retentionAmount",
      "onHoldAmount",
    ];
    const conditionTypeMap = {
      mobilizationAdvance: "Mobilization",
      retentionAmount: "Retention",
      onHoldAmount: "On Hold",
    };

    conditionTypes.forEach((conditionType, index) => {
      const original = originalPayment[conditionType];
      const current = currentPayment[conditionType];

      // console.log(`\n--- ${conditionTypeMap[conditionType]} Analysis ---`);
      console.log("Original:", original);
      console.log("Current:", current);

      // Check if this specific condition has changed
      const hasChanged = JSON.stringify(original) !== JSON.stringify(current);
      console.log("Has Changed (JSON comparison):", hasChanged);

      // Check if this condition has meaningful values (not just default zeros)
      const hasValues =
        current &&
        ((current.isPercentOfContract && current.percentageOfContract > 0) ||
          (!current.isPercentOfContract && current.advanceValue > 0) ||
          (!current.isPercentOfContract && current.retentionValue > 0) ||
          (!current.isPercentOfContract && current.holdAmountValue > 0) ||
          (current.isPercentOfRABill && current.percentageOfRABill > 0));
      console.log("Has Values:", hasValues);

      // Check if original condition existed and had values
      const originalHadValues =
        original &&
        ((original.isPercentOfContract && original.percentageOfContract > 0) ||
          (!original.isPercentOfContract && original.advanceValue > 0) ||
          (!original.isPercentOfContract && original.retentionValue > 0) ||
          (!original.isPercentOfContract && original.holdAmountValue > 0) ||
          (original.isPercentOfRABill && original.percentageOfRABill > 0));
      console.log("Original Had Values:", originalHadValues);

      // Only include if:
      // 1. It's a NEW condition (original had no values but current has values)
      // 2. It's a MODIFIED condition (both had values but they're different)
      const isNewCondition = !originalHadValues && hasValues;
      const isModifiedCondition = originalHadValues && hasValues && hasChanged;

      console.log("Is New Condition:", isNewCondition);
      console.log("Is Modified Condition:", isModifiedCondition);

      if (isNewCondition || isModifiedCondition) {
        console.log(`✅ INCLUDING  in update payload`);
        changedConditions.push(transformedPaymentConditions[index]);
      } else {
        console.log(`❌ EXCLUDING  from update payload`);
      }
    });

    console.log("\n=== FINAL CHANGED CONDITIONS ===");
    console.log("Changed Payment Conditions to send:", changedConditions);
    return changedConditions;
  };

  const submitForm = async (
    data: any,
    isDraft: boolean = false,
    isSubmitAction: boolean = false
  ) => {
    if (!validateFinalSubmission(data)) return;

    const isEditMode = data.type === "edit";

    try {
      setSubmitting(true);
      console.log("Work Order Form Data:", data);
      console.log("Is Edit Mode:", isEditMode);
      // Transform work order lines to API format
      const transformedWorkLines = workOrderLinesWatch.map((line: any) => {
        // Handle work level location fields - For Villa, send villa number in towerVilla
        const isVilla = line.workLevel === "Villa";

        // Transform stages for API
        const transformedStages =
          line.stages && line.stages.length > 0
            ? line.stages.map((stage: any) => {
              // For update: include workOrderLineStageId if it exists
              if (isEditMode && stage.workOrderLineStageId) {
                return {
                  workOrderLineStageId: stage.workOrderLineStageId,
                  stageTitle: stage.stageTitle,
                  splitPercentage: stage.splitPercentage,
                };
              }
              // For creation or new stages: only stageTitle and splitPercentage
              return {
                stageTitle: stage.stageTitle,
                splitPercentage: stage.splitPercentage,
              };
            })
            : [];

        // Extract location fields with proper type conversion before creating transformation object
        const towerVillaValue = line.towerVillaId || line.towerVilla;
        const flatValue =
          line.source === "Work BOQ"
            ? line.flatId || null
            : line.flatId || line.flatName || null;

        const transformedSiteStructureUnitId = towerVillaValue
          ? Number(towerVillaValue)
          : null;
        const transformedFloorNo = isVilla ? null : line.floorName || null;
        const transformedSiteStructureSubUnitId = isVilla
          ? null
          : flatValue
            ? Number(flatValue)
            : null;
        const transformedLocationId = line.locationId
          ? Number(line.locationId)
          : null;

        console.log(`Transforming location for line ${line.taskTitle}:`, {
          source: line.source,
          towerVillaId: line.towerVillaId,
          towerVilla: line.towerVilla,
          floorName: line.floorName,
          flatId: line.flatId,
          flatName: line.flatName,
          transformed: {
            siteStructureUnitId: transformedSiteStructureUnitId,
            floorNo: transformedFloorNo,
            siteStructureSubUnitId: transformedSiteStructureSubUnitId,
            locationId: transformedLocationId,
          },
          isVilla,
        });

        const baseTransformation: any = {
          sourceType:
            line.source === "Project Management"
              ? "Project Tasks"
              : line.source === "Work BOQ"
                ? "Work BOQ"
                : "New Work Line",
          sourceId: line.sourceId || null,
          workCategoryId:
            (line.workCategory &&
              line.workCategory !== "" &&
              Number(line.workCategory)) ||
            Number(line.workCategoryId) ||
            null,
          taskTitle: line.taskTitle || "",
          description: line.taskDescription || "",
          quantity: Number(line.quantity) || 0,
          uomId:
            (line.uom && line.uom !== "" && Number(line.uom)) ||
            Number(line.uomId) ||
            null,
          rate: Number(line.rate) || null,
          remarks: line.remarks || null,
          weightagePct: Number(line.weightagePercentage) || 0,
          // Budget allocation data
          ...(line.budgetLineId && { budgetLineId: line.budgetLineId }),
          ...(line.budgetAllocation && {
            budgetAllocation: Number(line.budgetAllocation),
          }),
          // Work Level Settings location fields (matching BOQ API format)
          // siteStructureUnitId = towerVilla ID (number)
          // floorNo = floorName (string)
          // siteStructureSubUnitId = flatName ID (number)
          // locationId = locationId (number)
          // For Project Management: towerVilla stores the ID (string or number)
          // For Work BOQ: towerVillaId stores the ID
          // For New Work Line: towerVilla stores the ID
          siteStructureUnitId: transformedSiteStructureUnitId,
          floorNo: transformedFloorNo,
          siteStructureSubUnitId: transformedSiteStructureSubUnitId,
          locationId: transformedLocationId,
          ...(line.scheduledStartDate && { scheduledStartDate: line.scheduledStartDate }),
          ...(line.scheduledEndDate && { scheduledEndDate: line.scheduledEndDate }),
        };

        // Add stages if present
        if (transformedStages.length > 0) {
          baseTransformation.stages = transformedStages;
          baseTransformation.hasStages = true;
        }

        // Add deletedStageIds for update mode
        if (
          isEditMode &&
          line.deletedStageIds &&
          line.deletedStageIds.length > 0
        ) {
          baseTransformation.deletedStageIds = line.deletedStageIds;
        }

        // Add tax information if present
        if (line.taxType && line.taxType !== "") {
          const gstRates = calculateGSTRates(line.taxPercentage || 0);

          console.log(`Line ${line.taskTitle} GST breakdown:`, gstRates);

          const taxData: any = {
            ...baseTransformation,
            taxId: Number(line.taxType),
            taxName: line.taxTypeName || "",
            taxRate: line.taxPercentage || 0,
          };

          // Include GST breakdown based on state comparison
          // For non-zero rates: only include non-zero fields
          // For zero rates: include the appropriate zero fields based on state
          if (gstRates.igstRate > 0) {
            // Different state - IGST only
            taxData.igstRate = gstRates.igstRate;
          } else if (gstRates.sgstRate > 0 || gstRates.cgstRate > 0) {
            // Same state - SGST and CGST
            taxData.sgstRate = gstRates.sgstRate;
            taxData.cgstRate = gstRates.cgstRate;
          } else {
            // Tax rate is 0% - include appropriate zero fields based on state
            const isSameState =
              currentUserState &&
              contractorState &&
              currentUserState === contractorState;
            if (isSameState) {
              taxData.sgstRate = 0;
              taxData.cgstRate = 0;
            } else {
              taxData.igstRate = 0;
            }
          }

          return taxData;
        }

        return baseTransformation;
      });

      // Transform payment conditions to API format
      const transformedPaymentConditions = paymentConditionsWatch
        ? [
          // Mobilization condition - include ID only if it exists (for updates)
          {
            ...(paymentConditionsWatch.mobilizationAdvance
              ?.workOrderPaymentTermId && {
              conditionId:
                paymentConditionsWatch.mobilizationAdvance
                  .workOrderPaymentTermId,
            }),
            conditionType: "Mobilization",
            isPercent:
              paymentConditionsWatch.mobilizationAdvance
                ?.isPercentOfContract || false,
            percentValue:
              paymentConditionsWatch.mobilizationAdvance
                ?.percentageOfContract || 0,
            amountValue:
              paymentConditionsWatch.mobilizationAdvance?.advanceValue || 0,
            interestCharged:
              paymentConditionsWatch.mobilizationAdvance?.interestCharged ||
              false,
            interestRate:
              paymentConditionsWatch.mobilizationAdvance?.interestRate || 0,
            recoveryType:
              paymentConditionsWatch.mobilizationAdvance?.recoveryType ||
              "INSTALLMENTS",
            recoveryPercentRate:
              paymentConditionsWatch.mobilizationAdvance
                ?.recoveryPercentRate || 0,
            recoveryCapPercent:
              paymentConditionsWatch.mobilizationAdvance
                ?.recoveryCapPercent || 0,
            releaseDate:
              paymentConditionsWatch.mobilizationAdvance?.releaseDate || null,
            notes: "Payment condition for mobilization advance",
          },
          // Retention condition - include ID only if it exists (for updates)
          {
            ...(paymentConditionsWatch.retentionAmount
              ?.workOrderPaymentTermId && {
              conditionId:
                paymentConditionsWatch.retentionAmount.workOrderPaymentTermId,
            }),
            conditionType: "Retention",
            isPercent:
              paymentConditionsWatch.retentionAmount?.isPercentOfRABill ||
              false,
            percentValue:
              paymentConditionsWatch.retentionAmount?.percentageOfRABill || 0,
            amountValue:
              paymentConditionsWatch.retentionAmount?.retentionValue || 0,
            interestCharged: false,
            interestRate: 0,
            recoveryType: "Final RA Bill",
            releaseDate:
              paymentConditionsWatch.retentionAmount?.releaseDate || null,
            notes: "Retention amount payment condition",
          },
          // On Hold condition - include ID only if it exists (for updates)
          {
            ...(paymentConditionsWatch.onHoldAmount
              ?.workOrderPaymentTermId && {
              conditionId:
                paymentConditionsWatch.onHoldAmount.workOrderPaymentTermId,
            }),
            conditionType: "On Hold",
            isPercent:
              paymentConditionsWatch.onHoldAmount?.isPercentOfContract ||
              false,
            percentValue:
              paymentConditionsWatch.onHoldAmount?.percentageOfContract || 0,
            amountValue:
              paymentConditionsWatch.onHoldAmount?.holdAmountValue || 0,
            interestCharged: false,
            interestRate: 0,
            recoveryType: "Final RA Bill",
            releaseDate:
              paymentConditionsWatch.onHoldAmount?.releaseDate || null,
            notes: "On hold amount payment condition",
          },
        ]
        : [];

      // Transform general conditions to API format
      const transformedGeneralConditions = generalConditionsWatch.map(
        (condition: any) => ({
          conditionCode: condition.id,
          title: condition.conditionName || "",
          content: condition.conditionContent || "",
          // order: condition.order || 1,
          // mandatory: condition.mandatory || false,
          // attachments: condition.attachments || [],
        })
      );

      // Determine status based on original status and user action
      let status: string;

      if (isEditMode && originalData) {
        const originalStatus = originalData.status;
        console.log("=== STATUS LOGIC ===");
        console.log("Original Status:", originalStatus);
        console.log("Is Draft Action:", isDraft);
        console.log("Is Submit Action:", isSubmitAction);

        if (originalStatus === "Changes Requested") {
          // If original status was "Changes Requested", always set to "Amended" on update
          status = "Amended";
          console.log("Status logic: Changes Requested → Amended");
        } else if (originalStatus === "Draft") {
          if (isDraft) {
            // User clicked "Save as Draft" - keep as Draft
            status = "Draft";
            console.log("Status logic: Draft + Save as Draft Action → Draft");
          } else if (isSubmitAction) {
            // User clicked "Submit Work Order" - set to Submitted
            status = "Submitted";
            console.log("Status logic: Draft + Submit Action → Submitted");
          } else {
            // Default case - keep as Draft
            status = "Draft";
            console.log("Status logic: Draft + Default Action → Draft");
          }
        } else {
          // For other statuses (Submitted, Approved, etc.), always set to appropriate status on update
          if (
            originalStatus === "Submitted" ||
            originalStatus === "Approved" ||
            originalStatus === "In Progress"
          ) {
            status = "Amended";
            console.log("Status logic: Non-Draft status → Amended");
          } else {
            // Fallback to default logic
            status = isDraft ? "Draft" : "Submitted";
            console.log(
              "Status logic: Other status, using default logic →",
              status
            );
          }
        }
      } else {
        // For create mode, use original logic
        status = isDraft ? "Draft" : "Submitted";
        // : "Draft";
        console.log("Status logic: Create mode →", status);
      }

      console.log("Final Status:", status);

      // Extract budgetHeaderId from form data (now stored directly in form)
      const budgetHeaderId = data.budgetHeaderId;
      console.log("Budget Header ID for submission:", budgetHeaderId);
      console.log("Complete form data:", data);

      // Parse approvals data if approvalRequired is true
      let approvalsArray: any[] | null = null;
      if (data.approvalRequired === "true" && data.approvalsData) {
        try {
          const parsedData = JSON.parse(data.approvalsData);
          approvalsArray = parsedData?.data || parsedData;
          console.log("Parsed approvals data:", approvalsArray);
        } catch (error) {
          console.warn("Failed to parse approvalsData:", error);
        }
      }

      // Read flowType from URL query parameters
      const searchParams = new URLSearchParams(location.search);
      const flowType = searchParams.get("flowType");

      // Build filesData array - map files to their corresponding lines
      const filesData: any[] = [];
      let fileOrder = 0;
      workOrderLinesWatch.forEach((line: any, lineIndex: number) => {
        const lineFiles = (line as any).files || [];
        lineFiles.forEach((file: any) => {
          // Only include new files (not existing files from API)
          const isNewFile = !(file.apiFileData && file.apiFileData.dmsId);
          if (isNewFile) {
            // Build filesData object - taskTitle is optional (only for line-level attachments)
            const fileDataEntry: any = {
              order: fileOrder++,
              module: "Work Order",
              moduleFileType: "Line Attachments",
            };
            // Include taskTitle only if it's a line-level attachment (when line has taskTitle)
            if (line.taskTitle) {
              fileDataEntry.taskTitle = line.taskTitle;
            }
            filesData.push(fileDataEntry);
          }
        });
      });

      // Prepare API submission data matching the required format
      const apiPayload = {
        // ...(data.workOrderNumber && { workOrderNumber: data.workOrderNumber }),
        workOrderNumber: data.numberingMode === 'auto' ? null : (data.workOrderNumber || null),
        linkedWorkRequestId: null, // No linked work request
        linkedTenderId: null, // No linked tender
        contractDate: data.contractDate
          ? dayjs(data.contractDate).format("YYYY-MM-DD")
          : null,
        siteId: Number(data.siteId),
        ...(budgetHeaderId && { budgetHeaderId: Number(budgetHeaderId) }),
        segmentLevel:
          data.workLevel && data.workLevel === "Tower / Block"
            ? "Tower"
            : data.workLevel || null,
        approvalRequired: data.approvalRequired == "true" ? true : false,
        ...(data.approvalRequired === "true" &&
          approvalsArray && { approvals: approvalsArray }),
        plannedEndDate: data.plannedEndDate
          ? dayjs(data.plannedEndDate).format("YYYY-MM-DD")
          : null,
        enableTax: data.enableTax === "true",
        plannedStartDate: data.plannedStartDate
          ? dayjs(data.plannedStartDate).format("YYYY-MM-DD")
          : null,
        contractType: data.contractType || "Construction",
        contractorId: data.contractorId ? Number(data.contractorId) : null,
        workOrderOwnerId: data.workOrderOwner
          ? Number(data.workOrderOwner)
          : null,
        status: status,
        projectId: data.projectId ? Number(data.projectId) : null,
        workType:
          data.workType && data.workType == "QTY" ? "Quantity" : data.workType,
        sourceType:
          data.selectedSource === "Project Management"
            ? "Project Tasks"
            : data.selectedSource === "Work BOQ"
              ? "Work BOQ"
              : "New Work Line",
        contractWith: data.contractWith || "GC",
        title: data.workOrderTitle || "",
        description: data.workOrderDescription || "",
        taxApplicationLevel: data.taxApplicationLevel || "Line",
        // Include tax information for submission
        ...(data.tdsTaxType &&
          data.tdsTaxType !== "" && {
          tdsTaxId: Number(data.tdsTaxType) || null,
          tdsRate: getTaxRateFromIdOrLabel(data.tdsTaxType),
          tdsTaxName: getTaxLabelFromId(data.tdsTaxType),
        }),
        ...(data.tcsTaxType &&
          data.tcsTaxType !== "" && {
          tcsTaxId: Number(data.tcsTaxType) || null,
          tcsRate: getTaxRateFromIdOrLabel(data.tcsTaxType),
          tcsTaxName: getTaxLabelFromId(data.tcsTaxType),
        }),
        paymentConditions: transformedPaymentConditions,
        generalConditions: transformedGeneralConditions,
        lines: transformedWorkLines,
        filesData: filesData, // Add filesData array for file metadata
        ...(flowType && { flowType: flowType }),
        isStrictApproval: data.isStrictApproval || false,
      };

      console.log("Work Order API Payload:", apiPayload);
      console.log("=== TDS/TCS SUBMISSION DEBUG ===");
      if (apiPayload.tdsTaxId) {
        console.log("TDS Info:", {
          tdsTaxId: apiPayload.tdsTaxId,
          tdsRate: apiPayload.tdsRate,
          tdsTaxName: apiPayload.tdsTaxName,
        });
      }
      if (apiPayload.tcsTaxId) {
        console.log("TCS Info:", {
          tcsTaxId: apiPayload.tcsTaxId,
          tcsRate: apiPayload.tcsRate,
          tcsTaxName: apiPayload.tcsTaxName,
        });
      }
      console.log("=== APPROVALS SUBMISSION DEBUG ===");
      if (apiPayload.approvals) {
        console.log("Approvals included in payload:", apiPayload.approvals);
      } else {
        console.log(
          "No approvals in payload (approvalRequired:",
          data.approvalRequired,
          ")"
        );
      }

      let response: any;

      if (isEditMode && originalData) {
        // Build incremental update payload with only changed data
        const updatePayload: any = {
          workOrderId: Number(data.id),
        };

        // Add changed basic fields (now originalData contains complete form data)
        const changedFields = getChangedFields(originalData, data);
        console.log("=== CHANGE DETECTION DEBUG ===");
        console.log("Original Complete Data:", originalData);
        console.log("Current Data:", data);
        console.log("Detected Changed Fields:", changedFields);

        Object.keys(changedFields).forEach((key) => {
          if (
            key === "contractDate" ||
            key === "plannedStartDate" ||
            key === "plannedEndDate"
          ) {
            updatePayload[key] = changedFields[key]
              ? dayjs(changedFields[key]).format("YYYY-MM-DD")
              : null;
          } else if (key === "linkedWorkRequest" || key === "linkedTender") {
            updatePayload[key + "Id"] = changedFields[key]
              ? Number(changedFields[key])
              : null;
          } else if (
            key === "siteId" ||
            key === "projectId" ||
            key === "contractorId"
          ) {
            updatePayload[key] = changedFields[key]
              ? Number(changedFields[key])
              : null;
          } else if (key === "workOrderOwner") {
            updatePayload["workOrderOwnerId"] = changedFields[key]
              ? Number(changedFields[key])
              : null;
          } else if (key === "workOrderTitle") {
            updatePayload["title"] = changedFields[key] || "";
          } else if (key === "workOrderDescription") {
            updatePayload["description"] = changedFields[key] || "";
          } else if (key === "approvalRequired" || key === "enableTax") {
            updatePayload[key] = changedFields[key] === "true";

            // If approvalRequired changed to true, include approvals data
            if (
              key === "approvalRequired" &&
              changedFields[key] === "true" &&
              approvalsArray
            ) {
              updatePayload.approvals = approvalsArray;
              console.log(
                "Including approvals due to approvalRequired field change:",
                approvalsArray
              );
            }
          } else if (key === "isStrictApproval") {
            updatePayload[key] = Boolean(changedFields[key]); // Add this line
          } else if (key === "tdsTaxType") {
            updatePayload["tdsTaxId"] = changedFields[key]
              ? Number(changedFields[key])
              : null;
            if (changedFields[key]) {
              updatePayload["tdsRate"] = getTaxRateFromIdOrLabel(
                changedFields[key]
              );
              updatePayload["tdsTaxName"] = getTaxLabelFromId(
                changedFields[key]
              );
            }
          } else if (key === "tcsTaxType") {
            updatePayload["tcsTaxId"] = changedFields[key]
              ? Number(changedFields[key])
              : null;
            if (changedFields[key]) {
              updatePayload["tcsRate"] = getTaxRateFromIdOrLabel(
                changedFields[key]
              );
              updatePayload["tcsTaxName"] = getTaxLabelFromId(
                changedFields[key]
              );
            }
          } else {
            updatePayload[key] = changedFields[key];
          }
        });

        // Determine if we have any significant changes
        const hasBasicFieldChanges = Object.keys(changedFields).length > 0;
        const hasWorkOrderLinesChanges = hasWorkOrderLinesChanged(
          originalData.workOrderLines,
          workOrderLinesWatch
        );
        const hasGeneralConditionsChanges = hasGeneralConditionsChanged(
          originalData.generalConditions,
          generalConditionsWatch
        );
        const hasPaymentConditionsChanges = hasPaymentConditionsChanged(
          originalData.paymentConditions,
          paymentConditionsWatch
        );
        const hasDeletions =
          deletedItemsTracker.workOrderLineIds.length > 0 ||
          deletedItemsTracker.generalConditionIds.length > 0 ||
          deletedItemsTracker.paymentConditionIds.length > 0;

        console.log("=== COMPREHENSIVE CHANGE ANALYSIS ===");
        console.log(
          "Original Work Order Lines Count:",
          originalData.workOrderLines.length
        );
        console.log(
          "Current Work Order Lines Count:",
          workOrderLinesWatch.length
        );
        console.log(
          "Original General Conditions Count:",
          originalData.generalConditions.length
        );
        console.log(
          "Current General Conditions Count:",
          generalConditionsWatch.length
        );

        // Only add status if there are actual changes or if status itself is changing
        const statusChanged = originalData.status !== status;
        if (
          statusChanged ||
          hasBasicFieldChanges ||
          hasWorkOrderLinesChanges ||
          hasGeneralConditionsChanges ||
          hasPaymentConditionsChanges ||
          hasDeletions
        ) {
          updatePayload.status = status;

          // Ensure approvalRequired is set when status changes to Submitted or Amended
          if (status === "Submitted" || status === "Amended") {
            updatePayload.approvalRequired =
              !isDraft && data.approvalRequired === "true";

            // Include approvals data if approvalRequired is true
            if (data.approvalRequired === "true" && approvalsArray) {
              updatePayload.approvals = approvalsArray;
              console.log(
                "Including approvals in update payload:",
                approvalsArray
              );
            }
          } else if (status === "Draft") {
            updatePayload.approvalRequired = false;
          }
        }

        // Handle work order lines changes - send only changed/new lines
        if (hasWorkOrderLinesChanges) {
          const changedLines = getChangedWorkOrderLines(
            originalData.workOrderLines,
            workOrderLinesWatch
          );
          if (changedLines.length > 0) {
            const transformedChangedLines = changedLines.map((line: any) => {
              // Transform stages for edit mode
              const transformedStagesEdit =
                line.stages && line.stages.length > 0
                  ? line.stages.map((stage: any) => {
                    if (stage.workOrderLineStageId) {
                      // Existing stage: include workOrderLineStageId
                      return {
                        workOrderLineStageId: stage.workOrderLineStageId,
                        stageTitle: stage.stageTitle,
                        splitPercentage: stage.splitPercentage,
                      };
                    } else {
                      // New stage: only stageTitle and splitPercentage
                      return {
                        stageTitle: stage.stageTitle,
                        splitPercentage: stage.splitPercentage,
                      };
                    }
                  })
                  : [];

              const baseTransformation: any = {
                workOrderLineId: line.workOrderLineId || null, // Include ID for updates
                sourceType:
                  line.source === "Project Management"
                    ? "Project Tasks"
                    : line.source === "Work BOQ"
                      ? "Work BOQ"
                      : "New Work Line",
                sourceId: line.sourceId || null,
                workCategoryId:
                  line.workCategory && line.workCategory !== ""
                    ? Number(line.workCategory)
                    : null,
                taskTitle: line.taskTitle || "",
                description: line.taskDescription || "",
                quantity: Number(line.quantity) || 0,
                uomId: line.uom && line.uom !== "" ? Number(line.uom) : null,
                rate: Number(line.rate) || 0,
                weightagePct: Number(line.weightagePercentage) || 0,
                // Work Level Settings location fields (matching BOQ API format)
                siteStructureUnitId: line.towerVilla
                  ? Number(line.towerVilla)
                  : Number(line.towerVillaName) || null,
                floorNo: line.floorName || null,
                siteStructureSubUnitId: line.flatName
                  ? Number(line.flatName)
                  : null,
                locationId: line.locationId ? Number(line.locationId) : null,
                ...(line.scheduledStartDate && { scheduledStartDate: line.scheduledStartDate }),
                ...(line.scheduledEndDate && { scheduledEndDate: line.scheduledEndDate }),
              };

              // Add stages if present
              if (transformedStagesEdit.length > 0) {
                baseTransformation.stages = transformedStagesEdit;
                baseTransformation.hasStages = true;
              }

              // Add deletedStageIds for update mode
              if (line.deletedStageIds && line.deletedStageIds.length > 0) {
                baseTransformation.deletedStageIds = line.deletedStageIds;
              }

              // Add tax information with GST breakdown if present
              if (line.taxType && line.taxType !== "") {
                const gstRates = calculateGSTRates(line.taxPercentage || 0);

                console.log(
                  `Edit mode - Line ${line.taskTitle} GST breakdown:`,
                  gstRates
                );

                const taxData: any = {
                  ...baseTransformation,
                  taxId: Number(line.taxType),
                  taxName: line.taxTypeName || "",
                  taxRate: line.taxPercentage || 0,
                };

                // Include GST breakdown based on state comparison
                // For non-zero rates: only include non-zero fields
                // For zero rates: include the appropriate zero fields based on state
                if (gstRates.igstRate > 0) {
                  // Different state - IGST only
                  taxData.igstRate = gstRates.igstRate;
                } else if (gstRates.sgstRate > 0 || gstRates.cgstRate > 0) {
                  // Same state - SGST and CGST
                  taxData.sgstRate = gstRates.sgstRate;
                  taxData.cgstRate = gstRates.cgstRate;
                } else {
                  // Tax rate is 0% - include appropriate zero fields based on state
                  const isSameState =
                    currentUserState &&
                    contractorState &&
                    currentUserState === contractorState;
                  if (isSameState) {
                    taxData.sgstRate = 0;
                    taxData.cgstRate = 0;
                  } else {
                    taxData.igstRate = 0;
                  }
                }

                return taxData;
              }

              return baseTransformation;
            });
            updatePayload.lines = transformedChangedLines;
          }
        }

        // Handle deleted work order lines (use tracked deletions)
        if (deletedItemsTracker.workOrderLineIds.length > 0) {
          updatePayload.deletedWorkOrderLineIds =
            deletedItemsTracker.workOrderLineIds;
          console.log(
            "Including deleted work order line IDs:",
            deletedItemsTracker.workOrderLineIds
          );
        }

        // Handle general conditions changes
        if (hasGeneralConditionsChanges) {
          updatePayload.generalConditions = transformedGeneralConditions;
        }

        // Handle deleted general conditions (use tracked deletions)
        if (deletedItemsTracker.generalConditionIds.length > 0) {
          updatePayload.deletedGeneralConditionIds =
            deletedItemsTracker.generalConditionIds;
          console.log(
            "Including deleted general condition IDs:",
            deletedItemsTracker.generalConditionIds
          );
        }

        // Handle payment conditions changes - only send changed conditions
        if (hasPaymentConditionsChanges) {
          const changedPaymentConditions = getChangedPaymentConditions(
            originalData.paymentConditions,
            paymentConditionsWatch,
            transformedPaymentConditions
          );

          if (changedPaymentConditions.length > 0) {
            updatePayload.paymentConditions = changedPaymentConditions;
          }
        } else {
          console.log(
            "❌ No payment condition changes detected - excluding from payload"
          );
        }

        // Handle deleted payment conditions (use tracked deletions)
        if (deletedItemsTracker.paymentConditionIds.length > 0) {
          updatePayload.deletedPaymentConditionIds =
            deletedItemsTracker.paymentConditionIds;
          console.log(
            "Including deleted payment condition IDs:",
            deletedItemsTracker.paymentConditionIds
          );
        }

        // Build filesData array for update - map files to their corresponding lines
        const updateFilesData: any[] = [];
        let updateFileOrder = 0;
        const currentFileIds: Set<number> = new Set(); // Use Set to avoid duplicates

        workOrderLinesWatch.forEach((line: any, lineIndex: number) => {
          const lineFiles = (line as any).files || [];
          lineFiles.forEach((file: any, fileIndex: number) => {
            // Track existing file IDs (files that are kept)
            // Check for docId first (from API response), then dmsId, then fileId
            // Check in the same places as original files to ensure consistency
            // Also check uid as it might contain the docId as a string (from mapping)
            let existingFileId =
              file.apiFileData?.docId ||
              file.apiFileData?.dmsId ||
              file.apiFileData?.fileId ||
              file.dmsId ||
              file.fileId ||
              file.docId ||
              file.response?.fileId;

            // If no ID found yet, check if uid is a valid numeric ID
            // (uid is set to docId.toString() in mapping, so it might be the docId)
            if (!existingFileId && file.uid) {
              const uidAsNumber =
                typeof file.uid === "string"
                  ? parseInt(file.uid, 10)
                  : file.uid;
              if (uidAsNumber && !isNaN(uidAsNumber) && uidAsNumber > 0) {
                existingFileId = uidAsNumber;
              }
            }

            if (existingFileId) {
              const fileIdNum =
                typeof existingFileId === "string"
                  ? parseInt(existingFileId, 10)
                  : existingFileId;
              if (fileIdNum && !isNaN(fileIdNum)) {
                currentFileIds.add(fileIdNum); // Use Set.add to avoid duplicates
              }
            } else {
              // Only include new files (not existing files from API)
              const isNewFile = !existingFileId;
              if (isNewFile) {
                // Build filesData object - taskTitle is optional (only for line-level attachments)
                const fileDataEntry: any = {
                  order: updateFileOrder++,
                  module: "Work Order",
                  moduleFileType: "Line Attachments",
                };
                // Include taskTitle only if it's a line-level attachment (when line has taskTitle)
                if (line.taskTitle) {
                  fileDataEntry.taskTitle = line.taskTitle;
                }
                updateFilesData.push(fileDataEntry);
              }
            }
          });
        });

        // Find deleted files by comparing original files with current files
        const deletedDocIds: number[] = [];
        if (originalData && originalData.workOrderLines) {
          originalData.workOrderLines.forEach(
            (originalLine: any, lineIndex: number) => {
              const originalLineFiles = (originalLine as any).files || [];
              originalLineFiles.forEach(
                (originalFile: any, fileIndex: number) => {
                  // Get file ID from original file (handle multiple possible formats)
                  // Check docId first (from API response), then dmsId, then fileId
                  const originalFileId =
                    originalFile.apiFileData?.docId ||
                    originalFile.apiFileData?.dmsId ||
                    originalFile.apiFileData?.fileId ||
                    originalFile.dmsId ||
                    originalFile.fileId ||
                    originalFile.docId ||
                    originalFile.response?.fileId;

                  // Skip if we couldn't find a valid ID (don't use uid as it might be generated)
                  if (!originalFileId) {
                    return;
                  }

                  // Convert to number if it's a string
                  const originalFileIdNum =
                    typeof originalFileId === "string"
                      ? parseInt(originalFileId, 10)
                      : originalFileId;

                  // Only add to deletedDocIds if:
                  // 1. It's a valid number
                  // 2. It's NOT in the current files (meaning it was deleted)
                  const isInCurrentFiles =
                    currentFileIds.has(originalFileIdNum);

                  if (
                    originalFileIdNum &&
                    !isNaN(originalFileIdNum) &&
                    !isInCurrentFiles // Use Set.has for O(1) lookup
                  ) {
                    // File was in original but not in current - it was deleted
                    deletedDocIds.push(originalFileIdNum);
                  }
                }
              );
            }
          );
        }

        // Always include filesData and deletedDocIds in update payload (even if empty arrays)
        updatePayload.filesData = updateFilesData;
        updatePayload.deletedDocIds = deletedDocIds;

        console.log("=== FINAL UPDATE PAYLOAD DEBUG ===");
        console.log("Deleted Items Tracker State:", deletedItemsTracker);
        console.log("Has Basic Field Changes:", hasBasicFieldChanges);
        console.log("Has Work Order Lines Changes:", hasWorkOrderLinesChanges);
        console.log(
          "Has General Conditions Changes:",
          hasGeneralConditionsChanges
        );
        console.log(
          "Has Payment Conditions Changes:",
          hasPaymentConditionsChanges
        );
        console.log("Has Deletions:", hasDeletions);
        console.log("Status Changed:", statusChanged);
        console.log("Changed Basic Fields:", changedFields);
        console.log("=== SENDING UPDATE PAYLOAD ===");
        console.log(
          "Incremental Update Payload (only changed data):",
          updatePayload
        );
        console.log("=== TDS/TCS UPDATE DEBUG ===");
        if (updatePayload.tdsTaxId) {
          console.log("TDS Update Info:", {
            tdsTaxId: updatePayload.tdsTaxId,
            tdsRate: updatePayload.tdsRate,
            tdsTaxName: updatePayload.tdsTaxName,
          });
        }
        if (updatePayload.tcsTaxId) {
          console.log("TCS Update Info:", {
            tcsTaxId: updatePayload.tcsTaxId,
            tcsRate: updatePayload.tcsRate,
            tcsTaxName: updatePayload.tcsTaxName,
          });
        }
        delete updatePayload.contractorName;
        delete updatePayload.workOrderOwnerName;

        updatePayload.workOrderNumber = originalData.workOrderNumber;
        const _formData = new FormData();
        _formData.append("workOrder", JSON.stringify(updatePayload));

        // Collect files from all lines (files are stored per line in line.files)
        const allFiles: any[] = [];
        workOrderLinesWatch.forEach((line: any) => {
          const lineFiles = (line as any).files || [];
          allFiles.push(...lineFiles);
        });

        // Fallback: Also collect from global files if any (for backward compatibility)
        const projectManagementFiles =
          (data as any).projectManagementFiles || [];
        const workBoqFiles = (data as any).workBoqFiles || [];
        const newWorkLineFiles = (data as any).newWorkLineFiles || [];
        const globalFiles: any[] = [
          ...projectManagementFiles,
          ...workBoqFiles,
          ...newWorkLineFiles,
        ];

        // Add global files that aren't already in line files
        globalFiles.forEach((file: any) => {
          const fileId = file.uid || file.name || file.fileName;
          const alreadyAdded = allFiles.some(
            (f: any) => (f.uid || f.name || f.fileName) === fileId
          );
          if (!alreadyAdded) {
            allFiles.push(file);
          }
        });

        // Separate new files (File objects) from existing files (from API)
        const newFilesToUpload: File[] = [];
        const existingFileIds: number[] = [];

        allFiles.forEach((file: any) => {
          // Check if file is from API (has apiFileData with dmsId)
          if (file.apiFileData && file.apiFileData.dmsId) {
            existingFileIds.push(file.apiFileData.dmsId);
          } else {
            // New file - extract actual File object
            const actualFile =
              file instanceof File ? file : file.originFileObj || file;
            if (actualFile instanceof File) {
              newFilesToUpload.push(actualFile);
            }
          }
        });

        // Append new files to FormData
        newFilesToUpload.forEach((file: File) => {
          _formData.append("files", file);
        });

        // Append existing file IDs if any (for update mode, backend will handle existing files)
        if (existingFileIds.length > 0) {
          _formData.append("existingFileIds", JSON.stringify(existingFileIds));
        }

        response = await workOrderApis.updateWorkOrder(_formData);
      } else {
        // For create, use FormData as before
        const _formData = new FormData();
        _formData.append("workOrder", JSON.stringify(apiPayload));

        // Collect files from all lines (files are stored per line in line.files)
        const allFiles: any[] = [];
        workOrderLinesWatch.forEach((line: any) => {
          const lineFiles = (line as any).files || [];
          allFiles.push(...lineFiles);
        });

        // Fallback: Also collect from global files if any (for backward compatibility)
        const projectManagementFiles =
          (data as any).projectManagementFiles || [];
        const workBoqFiles = (data as any).workBoqFiles || [];
        const newWorkLineFiles = (data as any).newWorkLineFiles || [];
        const globalFiles: any[] = [
          ...projectManagementFiles,
          ...workBoqFiles,
          ...newWorkLineFiles,
        ];

        // Add global files that aren't already in line files
        globalFiles.forEach((file: any) => {
          const fileId = file.uid || file.name || file.fileName;
          const alreadyAdded = allFiles.some(
            (f: any) => (f.uid || f.name || f.fileName) === fileId
          );
          if (!alreadyAdded) {
            allFiles.push(file);
          }
        });

        // For create mode, all files should be new File objects
        allFiles.forEach((file: any) => {
          // Extract actual File object if it's wrapped in an object
          const actualFile =
            file instanceof File ? file : file.originFileObj || file;
          if (actualFile instanceof File) {
            _formData.append("files", actualFile);
          }
        });

        response = await workOrderApis.createWorkOrder(_formData);
      }

      if (response && response.status) {
        // Dynamic success messages based on status and action
        let successMessage = "";
        let modalTitle = "";
        let modalText = "";

        if (isEditMode) {
          if (originalData?.status === "Changes Requested") {
            successMessage = "Work Order amended successfully!";
            modalTitle = "Work Order Amended Successfully!";
            modalText =
              "Your work order amendments have been submitted for review.";
          } else if (originalData?.status === "Draft") {
            if (isDraft) {
              successMessage = "Work Order saved as draft successfully!";
              modalTitle = "Work Order Saved as Draft!";
              modalText = "You can continue editing this work order later.";
            } else if (isSubmitAction) {
              successMessage = "Work Order submitted successfully!";
              modalTitle = "Work Order Submitted Successfully!";
              modalText = "Your work order has been submitted for approval.";
            } else {
              successMessage = "Work Order updated successfully!";
              modalTitle = "Work Order Updated Successfully!";
              modalText = "Your work order has been updated successfully.";
            }
          } else {
            // Non-draft statuses (Submitted, Approved, etc.)
            successMessage = "Work Order amended successfully!";
            modalTitle = "Work Order Amended Successfully!";
            modalText =
              "Your work order amendments have been submitted for review.";
          }
        } else {
          successMessage = isDraft
            ? "Work Order saved as draft successfully!"
            : "Work Order submitted successfully!";
          modalTitle = isDraft
            ? "Work Order Saved as Draft!"
            : "Work Order Submitted Successfully!";
          modalText = isDraft
            ? "You can continue editing this work order later."
            : "Your work order has been submitted for approval.";
        }

        message.success(successMessage);

        // Show success modal with API response information
        Swal.fire({
          title: modalTitle,
          text: modalText,
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "Go to Work Order List",
          cancelButtonText: "Create Another",
          confirmButtonColor: "#28a745",
          cancelButtonColor: "#6c757d",
        }).then((result) => {
          if (result.isConfirmed) {
            // Navigate to work order list
            const searchParams = new URLSearchParams(location.search);
            const flowType = searchParams.get("flowType");
            const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
            navigate(`/view-work-orders${flowTypeQuery}`);
          } else {
            // Navigate to create new work order
            const searchParams = new URLSearchParams(location.search);
            const flowType = searchParams.get("flowType");
            const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
            navigate(`/work-order-creation${flowTypeQuery}`);
          }
        });
      } else {
        throw new Error(response?.message || "Failed to create work order");
      }
    } catch (error: any) {
      console.error("Error submitting work order:", error);
      const errorMessage = isDraft
        ? "Failed to save work order as draft"
        : "Failed to create work order";
      message.error(errorMessage);
      Swal.fire({
        icon: "error",
        title: isDraft ? "Save Failed" : "Submission Failed",
        text:
          error.message ||
          (isDraft
            ? "Failed to save work order. Please try again."
            : "Failed to submit work order. Please try again."),
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to get tax label from tax ID
  const getTaxLabelFromId = (taxId: string): string => {
    if (!taxId) return "";

    // If it's already a label with rate format, return as is
    if (taxId.includes(" - ")) {
      return taxId;
    }

    // Look up the tax ID in our fetched tax options
    const taxOption = taxOptions.find((option) => option.value === taxId);
    if (taxOption) {
      return taxOption.label;
    }

    // Fallback if no mapping found
    console.warn(
      "Tax ID not found in options:",
      taxId,
      "Available options:",
      taxOptions
    );
    return `Tax ID: ${taxId}`;
  };

  // Helper function to get tax rate from tax ID or label
  const getTaxRateFromIdOrLabel = (taxIdOrLabel: string): number => {
    if (!taxIdOrLabel) return 0;

    // First, get the label (in case we have an ID)
    const taxLabel = getTaxLabelFromId(taxIdOrLabel);

    // Extract rate from label format - handle multiple hyphens
    // Look for the last occurrence of " - " followed by a percentage
    if (taxLabel.includes(" - ")) {
      const parts = taxLabel.split(" - ");
      // Check from the end to find the part with percentage
      for (let i = parts.length - 1; i >= 0; i--) {
        if (parts[i].includes("%")) {
          const rate = parseFloat(parts[i].replace("%", "")) || 0;
          console.log(`Extracted rate ${rate}% from: "${taxLabel}"`);
          return rate;
        }
      }
    }

    // Fallback: try to find any percentage in the entire string
    const percentageMatch = taxLabel.match(/(\d+(?:\.\d+)?)\s*%/);
    if (percentageMatch) {
      const rate = parseFloat(percentageMatch[1]) || 0;
      console.log(`Fallback: Extracted rate ${rate}% from: "${taxLabel}"`);
      return rate;
    }

    // Fallback if no rate can be extracted
    console.warn(
      "Cannot extract tax rate from:",
      taxIdOrLabel,
      "Resolved label:",
      taxLabel
    );
    return 0;
  };

  // Helper function to calculate GST rates based on state comparison
  const calculateGSTRates = (taxPercentage: number) => {
    const isSameState =
      currentUserState &&
      contractorState &&
      currentUserState === contractorState;

    console.log("=== GST CALCULATION ===");
    console.log("Current user state:", currentUserState);
    console.log("Contractor state:", contractorState);
    console.log("Same state:", isSameState);
    console.log("Tax percentage:", taxPercentage);

    if (isSameState) {
      // Same state: Split into SGST and CGST (50% each)
      const halfRate = taxPercentage / 2;
      return {
        sgstRate: halfRate,
        cgstRate: halfRate,
        igstRate: 0,
      };
    } else {
      // Different states: Use IGST (full rate)
      return {
        sgstRate: 0,
        cgstRate: 0,
        igstRate: taxPercentage,
      };
    }
  };

  // Calculate totals
  const subtotal = workOrderLinesWatch.reduce(
    (sum: any, line: any) => sum + line.quantity * line.rate,
    0
  );

  // Debug: Log work order lines tax data
  console.log("=== WORK ORDER LINES TAX DEBUG ===");
  console.log("Number of lines:", workOrderLinesWatch.length);
  workOrderLinesWatch.forEach((line: any, index: number) => {
    const lineSubtotal = (line.quantity || 0) * (line.rate || 0);
    const expectedTaxAmount =
      line.taxPercentage > 0 ? (lineSubtotal * line.taxPercentage) / 100 : 0;
    console.log(`Line ${index + 1}:`, {
      taskTitle: line.taskTitle,
      taxType: line.taxType,
      taxTypeName: line.taxTypeName,
      taxPercentage: line.taxPercentage,
      taxAmount: line.taxAmount,
      expectedTaxAmount: expectedTaxAmount,
      taxMismatch: Math.abs(line.taxAmount - expectedTaxAmount) > 0.01,
      totalAmount: line.totalAmount,
      quantity: line.quantity,
      rate: line.rate,
      lineSubtotal: lineSubtotal,
    });
  });
  console.log("=== END LINES DEBUG ===");

  // Calculate tax amount based on tax configuration
  let lineLevelTax = 0;
  let headerLevelTax = 0;
  let tdsAmount = 0;
  let tcsAmount = 0;

  if (formData.enableTax === "true") {
    if (
      formData.taxApplicationLevel === TaxApplicationLevel.Line ||
      formData.taxApplicationLevel === TaxApplicationLevel.Both
    ) {
      // Sum up line-level taxes from individual work order lines
      lineLevelTax = workOrderLinesWatch.reduce((sum: any, line: any) => {
        const lineTotal = sum + (line.taxAmount || 0);
        console.log("Line Tax Debug:", {
          lineTitle: line.taskTitle,
          taxType: line.taxType,
          taxPercentage: line.taxPercentage,
          taxAmount: line.taxAmount,
          runningTotal: lineTotal,
        });
        return lineTotal;
      }, 0);
    }

    if (
      formData.taxApplicationLevel === TaxApplicationLevel.Header ||
      formData.taxApplicationLevel === TaxApplicationLevel.Both
    ) {
      // Calculate header-level tax on subtotal (if header tax type and rate are defined)
      if (formData.headerTaxType) {
        const headerTaxRate = getTaxRateFromIdOrLabel(formData.headerTaxType);
        headerLevelTax =
          headerTaxRate > 0 ? (subtotal * headerTaxRate) / 100 : 0;
        console.log("Header Tax Debug:", {
          headerTaxType: formData.headerTaxType,
          headerTaxLabel: getTaxLabelFromId(formData.headerTaxType),
          headerTaxRate,
          headerLevelTax,
        });
      }

      // Calculate TDS amount for "Both" or "Header" level (TDS is typically a deduction)
      if (
        (formData.taxApplicationLevel === TaxApplicationLevel.Both ||
          formData.taxApplicationLevel === TaxApplicationLevel.Header) &&
        formData.tdsTaxType
      ) {
        const tdsRate = getTaxRateFromIdOrLabel(formData.tdsTaxType);
        tdsAmount = tdsRate > 0 ? (subtotal * tdsRate) / 100 : 0;
        console.log("TDS Tax Debug:", {
          tdsTaxType: formData.tdsTaxType,
          tdsTaxLabel: getTaxLabelFromId(formData.tdsTaxType),
          tdsRate,
          tdsAmount,
          taxLevel: formData.taxApplicationLevel,
        });
      }

      // Calculate TCS amount for "Both" or "Header" level (TCS is typically an addition)
      // TCS is calculated on subtotal + GST (lineLevelTax + headerLevelTax)
      if (
        (formData.taxApplicationLevel === TaxApplicationLevel.Both ||
          formData.taxApplicationLevel === TaxApplicationLevel.Header) &&
        formData.tcsTaxType
      ) {
        const tcsRate = getTaxRateFromIdOrLabel(formData.tcsTaxType);
        const subtotalWithGST = subtotal + lineLevelTax + headerLevelTax;
        tcsAmount = tcsRate > 0 ? (subtotalWithGST * tcsRate) / 100 : 0;
        console.log("TCS Tax Debug:", {
          tcsTaxType: formData.tcsTaxType,
          tcsTaxLabel: getTaxLabelFromId(formData.tcsTaxType),
          tcsRate,
          subtotal,
          gstAmount: lineLevelTax + headerLevelTax,
          subtotalWithGST,
          tcsAmount,
          taxLevel: formData.taxApplicationLevel,
        });
      }
    }
  }

  const totalTaxAmount = lineLevelTax + headerLevelTax + tcsAmount; // TDS is deducted, not added
  const totalDeductionAmount = tdsAmount;
  const grossTotal = subtotal + totalTaxAmount - totalDeductionAmount;

  // Calculate GST slab-wise breakdown
  const gstSlabBreakdown: {
    [key: string]: {
      cgst: number;
      sgst: number;
      igst: number;
      totalAmount: number;
    };
  } = {};

  if (
    formData.enableTax === "true" &&
    (formData.taxApplicationLevel === TaxApplicationLevel.Line ||
      formData.taxApplicationLevel === TaxApplicationLevel.Both)
  ) {
    workOrderLinesWatch.forEach((line: any) => {
      if (line.taxPercentage && line.taxPercentage > 0 && line.taxAmount > 0) {
        const rate = line.taxPercentage;
        const rateKey = `${rate}%`;

        if (!gstSlabBreakdown[rateKey]) {
          gstSlabBreakdown[rateKey] = {
            cgst: 0,
            sgst: 0,
            igst: 0,
            totalAmount: 0,
          };
        }

        // Always calculate GST rates based on current user and contractor states
        // This ensures the breakdown reflects current state comparison, not historical data
        const gstRates = calculateGSTRates(line.taxPercentage);

        // Calculate individual GST component amounts
        const lineSubtotal = (line.quantity || 0) * (line.rate || 0);
        if (gstRates.igstRate > 0) {
          gstSlabBreakdown[rateKey].igst +=
            (lineSubtotal * gstRates.igstRate) / 100;
        } else {
          gstSlabBreakdown[rateKey].cgst +=
            (lineSubtotal * gstRates.cgstRate) / 100;
          gstSlabBreakdown[rateKey].sgst +=
            (lineSubtotal * gstRates.sgstRate) / 100;
        }
        gstSlabBreakdown[rateKey].totalAmount += line.taxAmount;
      }
    });
  }

  // Calculate TDS slab-wise breakdown (currently only one TDS rate at header level)
  const tdsSlabBreakdown: { [key: string]: number } = {};
  if (tdsAmount > 0 && formData.tdsTaxType) {
    const tdsRate = getTaxRateFromIdOrLabel(formData.tdsTaxType);
    const tdsLabel = getTaxLabelFromId(formData.tdsTaxType);
    if (tdsRate > 0) {
      tdsSlabBreakdown[tdsLabel || `TDS ${tdsRate}%`] = tdsAmount;
    }
  }

  // Calculate total budget allocation
  const totalBudgetAllocation = workOrderLinesWatch.reduce(
    (sum: number, line: any) => sum + (line.budgetAllocation || 0),
    0
  );

  // Debug logging for tax calculations
  console.log("=== TAX CALCULATION SUMMARY ===");
  console.log("Subtotal:", subtotal);
  console.log("Tax Enabled:", formData.enableTax);
  console.log("Tax Application Level:", formData.taxApplicationLevel);
  console.log(
    "Header Tax Type:",
    formData.headerTaxType,
    "->",
    getTaxLabelFromId(formData.headerTaxType)
  );
  console.log(
    "TDS Tax Type:",
    formData.tdsTaxType,
    "->",
    getTaxLabelFromId(formData.tdsTaxType)
  );
  console.log(
    "TCS Tax Type:",
    formData.tcsTaxType,
    "->",
    getTaxLabelFromId(formData.tcsTaxType)
  );
  console.log("--- Calculated Amounts ---");
  console.log("Line Level Tax:", lineLevelTax);
  console.log("Header Level Tax:", headerLevelTax);
  console.log("TDS Amount:", tdsAmount);
  console.log("TCS Amount:", tcsAmount);
  console.log("Total Tax Amount:", totalTaxAmount);
  console.log("Total Deduction Amount:", totalDeductionAmount);
  console.log("Gross Total:", grossTotal);
  console.log("=== END TAX CALCULATION ===");

  // Get readable names for IDs - will use display names from form data
  const getSiteName = (siteId: string) => {
    return formData.siteName || siteId;
  };

  const getProjectName = (projectId: string) => {
    return formData.projectName || projectId;
  };

  const getContractorName = (contractorId: string) => {
    return formData.contractorName || contractorId;
  };

  const getUserName = (userId: string) => {
    return formData.workOrderOwnerName || userId;
  };

  // Enhanced expandable row renderer
  const expandedRowRender = (record: WorkOrderLine) => {
    const hasDetails =
      record.taskDescription && record.taskDescription !== record.taskTitle;
    const hasWeightage =
      record.weightagePercentage && record.weightagePercentage > 0;
    const hasTax =
      record.taxType && record.taxPercentage && record.taxPercentage > 0;

    // Get files for this line from line object itself
    let lineFiles: any[] = (record as any).files || [];

    // Fallback to global files if line doesn't have files (for backward compatibility)
    if (lineFiles.length === 0) {
      const formData = getValues();
      if (record.source === WorkOrderLineSource.ProjectManagement) {
        lineFiles = (formData as any).projectManagementFiles || [];
      } else if (record.source === WorkOrderLineSource.WorkBOQ) {
        lineFiles = (formData as any).workBoqFiles || [];
      } else if (record.source === WorkOrderLineSource.NewWorkLine) {
        lineFiles = (formData as any).newWorkLineFiles || [];
      }
    }
    const hasFiles = lineFiles.length > 0;

    if (!hasDetails && !hasWeightage && !hasTax && !hasFiles) {
      return null;
    }

    return (
      <div
        style={{
          margin: "16px 0",
          backgroundColor: "#fafafa",
          padding: "16px",
          borderRadius: "6px",
        }}
      >
        {hasDetails && (
          <div className="mb-3">
            <strong>📝 Task Description:</strong>
            <p className="mt-1 mb-0">{record.taskDescription}</p>
          </div>
        )}

        <div className="row">
          {hasWeightage && (
            <div className="col-md-4">
              <strong>📊 Weightage:</strong>
              <div className="mt-1">
                <Tag color="blue">{record.weightagePercentage}%</Tag>
              </div>
            </div>
          )}

          {hasTax && (
            <div className="col-md-4">
              <strong>💰 Tax Details:</strong>
              <div className="mt-1">
                <Tag color="orange">
                  {record.taxType} ({record.taxPercentage}%)
                </Tag>
                <br />
                <small>Tax Amount: ₹{record.taxAmount?.toLocaleString("en-IN")}</small>
              </div>
            </div>
          )}

          <div className="col-md-4">
            <strong>📋 Source:</strong>
            <div className="mt-1">
              <Tag
                color={
                  record.source === WorkOrderLineSource.ProjectManagement
                    ? "green"
                    : record.source === WorkOrderLineSource.WorkBOQ
                      ? "blue"
                      : "orange"
                }
              >
                {record.source}
              </Tag>
            </div>
          </div>
        </div>

        {hasFiles && (
          <div className="mt-3 pt-3 border-top">
            <strong>
              <PaperClipOutlined className="me-2" />
              Attachments ({lineFiles.length}):
            </strong>
            <div className="mt-2">
              {lineFiles.map((file: any, index: number) => {
                const fileName =
                  file.name ||
                  file.fileName ||
                  file.response?.fileName ||
                  file.apiFileData?.fileName ||
                  `File ${index + 1}`;
                return (
                  <div
                    key={file.uid || index}
                    className="mb-2 d-flex align-items-center"
                  >
                    <FileTextOutlined className="me-2 text-muted" />
                    <span className="text-muted">{fileName}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Dynamic columns for work order lines preview table
  const getWorkOrderLinesColumns = () => {
    // Check if any line has a source other than WorkBOQ (to show Weightage column)
    const hasNonBoqLines = workOrderLinesWatch.some(
      (line: any) => line.source !== WorkOrderLineSource.WorkBOQ
    );

    // Check if any line is from Project Management source or Work BOQ (to show Target Rate column)
    const hasPMLines = workOrderLinesWatch.some(
      (line: any) => line.source === WorkOrderLineSource.ProjectManagement
    );
    const hasBOQLines = workOrderLinesWatch.some(
      (line: any) => line.source === WorkOrderLineSource.WorkBOQ
    );
    const hasTargetRate = hasPMLines || hasBOQLines;

    // Check if tax is enabled and at what level
    const taxEnabled = formData.enableTax === "true";
    const showLineTax =
      taxEnabled &&
      (formData.taxApplicationLevel === TaxApplicationLevel.Line ||
        formData.taxApplicationLevel === TaxApplicationLevel.Both);

    const baseColumns = [
      {
        title: "Line #",
        dataIndex: "lineNumber",
        key: "lineNumber",
        width: 80,
      },
      {
        title: "Task Title",
        dataIndex: "taskTitle",
        key: "taskTitle",
        width: 200,
        render: (text: string, record: WorkOrderLine) => (
          <div>
            <div className="fw-bold">{text}</div>
            {record.taskDescription && record.taskDescription !== text && (
              <small className="text-muted">
                {record.taskDescription.substring(0, 50)}...
              </small>
            )}
          </div>
        ),
      },
      {
        title: "Category",
        dataIndex: "workCategory",
        key: "workCategory",
        width: 150,
        render: (category: string, record: WorkOrderLine) => (
          <div>
            <div>{record.workCategoryName || category}</div>
            {record.subCategory && (
              <small className="text-muted">{record.subCategory}</small>
            )}
          </div>
        ),
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        width: 100,
        render: (qty: number, record: any) => (
          <div>
            <strong>
              {qty?.toLocaleString("en-IN")} {record.uomName || record.uom}
            </strong>
          </div>
        ),
      },
      // Show Target Rate for Project Management and Work BOQ sources
      ...(hasTargetRate
        ? [
          {
            title: "Target Rate",
            key: "targetRate",
            width: 120,
            render: (_: any, record: WorkOrderLine) => {
              if (record.source === WorkOrderLineSource.ProjectManagement) {
                const taskRate = Number(record.taskRate) || 0;
                return (
                  <div style={{ textAlign: "right" }}>
                    <span>₹{taskRate.toLocaleString("en-IN")}</span>
                  </div>
                );
              } else if (record.source === WorkOrderLineSource.WorkBOQ) {
                const boqRate = Number(record.boqRate) || 0;
                return (
                  <div style={{ textAlign: "right" }}>
                    <span>₹{boqRate.toLocaleString("en-IN")}</span>
                  </div>
                );
              }
              return <span className="text-muted">-</span>;
            },
          },
        ]
        : []),
      {
        title: hasTargetRate ? "Execution Rate" : "Rate",
        dataIndex: "rate",
        key: "rate",
        width: hasTargetRate ? 150 : 100,
        render: (rate: number, record: WorkOrderLine) => {
          const hasRemarks =
            (record.source === WorkOrderLineSource.ProjectManagement ||
              record.source === WorkOrderLineSource.WorkBOQ) &&
            (record.rateChangeRemarks || record.remarks);

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>₹{rate?.toLocaleString("en-IN")}</span>
              {hasRemarks && (
                <Tooltip
                  title={record.rateChangeRemarks || record.remarks}
                  placement="top"
                >
                  <InfoCircleOutlined
                    style={{
                      color: "#1890ff",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Total Amount",
        key: "totalAmount",
        width: 120,
        render: (_: any, record: WorkOrderLine) => (
          <div>
            <strong>₹{record.totalAmount?.toLocaleString("en-IN")}</strong>
            {!showLineTax && record.taxAmount && record.taxAmount > 0 && (
              <div>
                <small className="text-muted">
                  Tax: ₹{record.taxAmount?.toLocaleString("en-IN")}
                </small>
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Location",
        key: "location",
        width: 180,
        render: (text: any, record: any) => {
          // Helper function to check if a value is meaningful (not null, undefined, or empty string)
          const hasValue = (val: any): boolean => {
            if (val === null || val === undefined) return false;
            const str = String(val).trim();
            return str !== "" && str !== "null" && str !== "undefined";
          };

          // Show only locationName
          const locationName = hasValue(record.locationName)
            ? String(record.locationName).trim()
            : "-";

          return <span className="fw-bold">{locationName}</span>;
        },
      },
      {
        title: "Docs",
        key: "docs",
        width: 100,
        render: (_: any, record: WorkOrderLine) => {
          // Get files for this line from line object itself
          let lineFiles: any[] = (record as any).files || [];

          // Fallback to global files if line doesn't have files (for backward compatibility)
          if (lineFiles.length === 0) {
            const formData = getValues();
            if (record.source === WorkOrderLineSource.ProjectManagement) {
              lineFiles = (formData as any).projectManagementFiles || [];
            } else if (record.source === WorkOrderLineSource.WorkBOQ) {
              lineFiles = (formData as any).workBoqFiles || [];
            } else if (record.source === WorkOrderLineSource.NewWorkLine) {
              lineFiles = (formData as any).newWorkLineFiles || [];
            }
          }

          if (lineFiles.length === 0) {
            return <span className="text-muted">-</span>;
          }

          const content = (
            <div style={{ minWidth: "200px" }}>
              {lineFiles.map((file: any, index: number) => {
                const fileName =
                  file.name ||
                  file.fileName ||
                  file.response?.fileName ||
                  file.apiFileData?.fileName ||
                  `File ${index + 1}`;
                const truncatedName =
                  fileName.length > 25
                    ? `${fileName.substring(0, 25)}...`
                    : fileName;
                return (
                  <div
                    key={file.uid || index}
                    style={{
                      padding: "8px 0",
                      borderBottom:
                        index < lineFiles.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <EyeOutlined
                      style={{ color: "#1890ff", fontSize: "16px" }}
                    />
                    <Tooltip
                      title={fileName}
                      placement="top"
                      overlayStyle={{ zIndex: 9999 }}
                      getPopupContainer={(trigger) =>
                        trigger.parentElement || document.body
                      }
                    >
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          const fileUrl =
                            file.url ||
                            file.fileUrl ||
                            file.filePath ||
                            file.apiFileData?.fileUrl ||
                            file.apiFileData?.url ||
                            file.apiFileData?.filePath ||
                            file.apiFileData?.documentUrl ||
                            file.response?.fileUrl ||
                            file.response?.url ||
                            file.response?.filePath ||
                            file.response?.documentUrl;

                          if (fileUrl) {
                            window.open(fileUrl, "_blank");
                          } else {
                            // Try to construct URL from dmsId if available
                            const dmsId =
                              file.apiFileData?.dmsId ||
                              file.apiFileData?.fileId ||
                              file.apiFileData?.docId ||
                              file.response?.fileId ||
                              file.response?.dmsId ||
                              file.response?.docId ||
                              file.uid;

                            // For new files (File objects), create blob URL to preview
                            const fileToPreview =
                              file instanceof File
                                ? file
                                : file.originFileObj instanceof File
                                  ? file.originFileObj
                                  : null;
                            if (fileToPreview) {
                              const blobUrl =
                                URL.createObjectURL(fileToPreview);
                              window.open(blobUrl, "_blank");
                              // Clean up blob URL after a delay
                              setTimeout(
                                () => URL.revokeObjectURL(blobUrl),
                                100
                              );
                              return;
                            }

                            // For existing files, try to construct URL from dmsId
                            if (
                              dmsId &&
                              (typeof dmsId === "number" ||
                                (typeof dmsId === "string" &&
                                  !dmsId.includes("-") &&
                                  !dmsId.startsWith("wol-")))
                            ) {
                              // Construct download URL - try common API patterns
                              const baseUrl =
                                process.env.REACT_APP_API_BASE_URL ||
                                window.location.origin;
                              // Try different URL patterns
                              const possibleUrls = [
                                `${baseUrl}/api/dms/${dmsId}/download`,
                                `${baseUrl}/api/dms/${dmsId}/view`,
                                `${baseUrl}/api/files/${dmsId}/download`,
                                `${baseUrl}/api/files/${dmsId}`,
                              ];

                              // Try first URL pattern
                              window.open(possibleUrls[0], "_blank");
                            } else {
                              message.info(
                                "File URL not available. Please save the work order first."
                              );
                            }
                          }
                        }}
                        style={{
                          fontSize: "14px",
                          color: "#1890ff",
                          cursor: "pointer",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "200px",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = "none";
                        }}
                      >
                        {truncatedName}
                      </span>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          );

          return (
            <Popover
              content={content}
              title={`Documents (${lineFiles.length})`}
              trigger="hover"
              placement="left"
            >
              <AntdButton
                type="link"
                style={{
                  padding: 0,
                  height: "auto",
                  fontWeight: "normal",
                }}
              >
                View
              </AntdButton>
            </Popover>
          );
        },
      },
      {
        title: "Source",
        dataIndex: "source",
        key: "source",
        width: 100,
        render: (source: WorkOrderLineSource) => (
          <Tag
            color={
              source === WorkOrderLineSource.ProjectManagement
                ? "green"
                : source === WorkOrderLineSource.WorkBOQ
                  ? "blue"
                  : "orange"
            }
          >
            {source === WorkOrderLineSource.ProjectManagement
              ? "PM"
              : source === WorkOrderLineSource.WorkBOQ
                ? "BOQ"
                : "New"}
          </Tag>
        ),
      },
    ];

    // Add Weightage column only if there are non-BOQ lines
    if (hasNonBoqLines) {
      // Find the index to insert Weightage column (after Rate, before Total Amount)
      const rateColumnIndex = baseColumns.findIndex(
        (col) => col.key === "rate"
      );
      const weightageColumn = {
        title: "Weightage",
        dataIndex: "weightagePercentage",
        key: "weightagePercentage",
        width: 90,
        render: (weightage: number, record: WorkOrderLine) => {
          // Show dash for BOQ sources (no weightage)
          if (record.source === WorkOrderLineSource.WorkBOQ) {
            return <span className="text-muted">-</span>;
          }
          return (
            <Tag color={weightage > 0 ? "blue" : "default"}>
              {weightage || 0}%
            </Tag>
          );
        },
      };
      baseColumns.splice(rateColumnIndex + 1, 0, weightageColumn);
    }

    // Add Tax column conditionally based on tax configuration
    if (showLineTax) {
      // Find the index to insert Tax column (after Weightage if exists, otherwise after Rate)
      let insertIndex = baseColumns.findIndex(
        (col) => col.key === "totalAmount"
      );

      const taxColumn = {
        title: "Tax",
        key: "tax",
        width: 180,
        render: (_: any, record: any) => {
          const hasTax = record.taxType && record.taxPercentage > 0;
          if (!hasTax) {
            return <span className="text-muted">-</span>;
          }

          const gstRates = calculateGSTRates(record.taxPercentage || 0);

          return (
            <div>
              <div>
                <Tag color="orange">
                  {record.taxTypeName || getTaxLabelFromId(record.taxType)}
                </Tag>
              </div>
              {gstRates.igstRate > 0 ? (
                // Different states - show IGST
                <div>
                  <small className="text-muted">
                    IGST: {gstRates.igstRate}%
                  </small>
                  <br />
                  <small className="text-success">
                    ₹{record.taxAmount?.toLocaleString("en-IN")}
                  </small>
                </div>
              ) : (
                // Same state - show CGST + SGST
                <div>
                  <small className="text-muted">
                    CGST: {gstRates.cgstRate}% | SGST: {gstRates.sgstRate}%
                  </small>
                  <br />
                  <small className="text-success">
                    ₹{((record.taxAmount || 0) / 2).toLocaleString("en-IN")} + ₹
                    {((record.taxAmount || 0) / 2).toLocaleString("en-IN")}
                  </small>
                </div>
              )}
            </div>
          );
        },
      };
      baseColumns.splice(insertIndex, 0, taxColumn);
    }

    // Add budget allocation columns
    const budgetLineColumn = {
      title: "Budget Line",
      key: "budgetLine",
      width: 160,
      render: (_: any, record: any) => {
        if (!record.budgetLineName) {
          return <span className="text-muted">No Budget Line</span>;
        }
        return (
          <div>
            <div style={{ fontWeight: "bold", fontSize: "12px" }}>
              {record.budgetLineName}
            </div>
            <small className="text-muted">
              ID: {record.budgetLineId || "N/A"}
            </small>
          </div>
        );
      },
    };

    const budgetAllocationColumn = {
      title: "Allocated Budget",
      key: "budgetAllocation",
      width: 120,
      render: (_: any, record: any) => {
        const budgetAmount = record.budgetAllocation || 0;
        if (budgetAmount === 0) {
          return <span className="text-muted">₹0</span>;
        }
        return (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: "bold", color: "#52c41a" }}>
              ₹{budgetAmount.toLocaleString("en-IN")}
            </div>
            {record.budgetLineName && (
              <small className="text-muted" style={{ fontSize: "10px" }}>
                {record.budgetLineName}
              </small>
            )}
          </div>
        );
      },
    };

    // Insert budget columns before the total amount column
    const totalAmountIndex = baseColumns.findIndex(
      (col) => col.key === "totalAmount"
    );
    if (totalAmountIndex !== -1) {
      baseColumns.splice(
        totalAmountIndex,
        0,
        //  budgetLineColumn,
        budgetAllocationColumn
      );
    }

    return baseColumns;
  };

  const ApprovalTimelineContent = () => {
    // Parse the approvalsData string to an object
    let approvalsData;
    try {
      approvalsData = formData?.approvalsData
        ? JSON.parse(formData.approvalsData)
        : null;
    } catch (error) {
      console.error("Error parsing approvalsData:", error);
      approvalsData = null;
    }

    const isStrictMode = formData?.isStrictApproval || false;

    // Check if approvalsData exists and has data array
    if (
      !approvalsData ||
      !approvalsData.data ||
      approvalsData.data.length === 0 ||
      formData?.approvalRequired !== "true"
    ) {
      return <Badge color="outline-danger">No</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.data?.map((approval: any, index: number) => {
              const isApproved = approval.status?.toLowerCase() == "approved";
              const isRejected = approval.status?.toLowerCase() == "rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.userName}
                  <div className="designation">{approval?.roleName}</div>
                  {approval?.actionDate && (
                    <div className="designation">
                      {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                  {approval.approvalStage && (
                    <div className="text-muted small">
                      <Tag
                        color="blue"
                        style={{ fontSize: "10px" }}
                      >
                        {approval.approvalStage}
                      </Tag>
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer"
        }}>
          <span
            style={{
              color: "#2263b3",
              // textDecoration: "underline",
            }}
          >
            <Badge color="outline-success">Yes</Badge>
          </span>

          {isStrictMode && (
            <Badge
              color="warning"
              style={{
                fontSize: "11px",
                cursor: "pointer",
                marginLeft: "4px"
              }}
              title="Sequential approvals required, Approvals must follow the sequence"
            >
              Strict
            </Badge>
          )}
        </div>
      </Popover>
    );
  };

  return (
    <>
      <form
        className="content clearfix"
        onSubmit={(event) => handleSubmit(() => submitForm(event, true))}
      >
        {/* <div className="mb-4">
          <div className="alert alert-success">
            <CheckCircleOutlined className="me-2" />
            <strong>Work Order Ready for Submission</strong>
            <br />
            <small>Please review all details below before final submission</small>
          </div>
        </div> */}

        {/* Section A: General Information Summary */}
        <Card title="📋 General Information Summary" className="mb-4">
          <Descriptions bordered size="small" column={2}>
            {formData.workOrderNumber && (
              <Descriptions.Item label="Work Order Number">
                {formData.workOrderNumber}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Contract Date">
              {formData.contractDate
                ? dayjs(formData.contractDate).format("DD-MMM-YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Work Order Title" span={2}>
              {formData.workOrderTitle}
            </Descriptions.Item>
            <Descriptions.Item label="Site">
              {getSiteName(formData.siteId)}
            </Descriptions.Item>
            <Descriptions.Item label="Project">
              {formData.projectId
                ? getProjectName(formData.projectId)
                : "Not selected"}
            </Descriptions.Item>
            <Descriptions.Item label="Contract Type">
              {formData.contractType}
            </Descriptions.Item>
            <Descriptions.Item label="Contract With">
              {formData.contractWith}
            </Descriptions.Item>
            <Descriptions.Item label={isClientFlow ? "Client" : "Contractor"}>
              {formData.contractorId
                ? getContractorName(formData.contractorId)
                : "Not selected"}
            </Descriptions.Item>
            <Descriptions.Item label="Work Order Owner">
              {formData.workOrderOwner
                ? getUserName(formData.workOrderOwner)
                : "Not selected"}
            </Descriptions.Item>
            <Descriptions.Item label="Planned Start">
              {formData.plannedStartDate
                ? dayjs(formData.plannedStartDate).format("DD-MMM-YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Planned End">
              {formData.plannedEndDate
                ? dayjs(formData.plannedEndDate).format("DD-MMM-YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Approval Required">
              {formData?.approvalsData && (
                <ApprovalTimelineContent />
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Tax Enabled" span={2}>
              <Tag color={formData.enableTax === "true" ? "blue" : "default"}>
                {formData.enableTax === "true"
                  ? `Yes (${formData.taxApplicationLevel})`
                  : "No"}
              </Tag>
              {formData.enableTax === "true" &&
                (formData.taxApplicationLevel === TaxApplicationLevel.Header ||
                  formData.taxApplicationLevel === TaxApplicationLevel.Both) &&
                formData.headerTaxType && (
                  <div className="mt-2">
                    <small className="text-muted">Header Tax Type: </small>
                    <Tag color="cyan">
                      {getTaxLabelFromId(formData.headerTaxType)}
                    </Tag>
                  </div>
                )}
              {formData.enableTax === "true" &&
                (formData.taxApplicationLevel === TaxApplicationLevel.Both ||
                  formData.taxApplicationLevel ===
                  TaxApplicationLevel.Header) && (
                  <div className="mt-2">
                    {formData.tdsTaxType && (
                      <div>
                        <small className="text-muted">TDS: </small>
                        <Tag color="orange">
                          {getTaxLabelFromId(formData.tdsTaxType)}
                        </Tag>
                        <small className="text-info">
                          {" "}
                          (Deducted from sub total)
                        </small>
                      </div>
                    )}
                    {formData.tcsTaxType && (
                      <div>
                        <small className="text-muted">TCS: </small>
                        <Tag color="purple">
                          {getTaxLabelFromId(formData.tcsTaxType)}
                        </Tag>
                        <small className="text-success">
                          {" "}
                          (Deducted from gross total)
                        </small>
                      </div>
                    )}
                  </div>
                )}
            </Descriptions.Item>
          </Descriptions>

          {formData.workOrderDescription && (
            <div className="mt-3">
              <strong>Description:</strong>
              <p className="mt-1 text-muted">{formData.workOrderDescription}</p>
            </div>
          )}
        </Card>

        {/* Section B: Work Order Lines Summary */}
        <Card title="📋 Work Order Lines Summary" className="mb-4">
          <div className="mb-3">
            <Tag color="blue">{workOrderLinesWatch.length} line(s)</Tag>
            <Tag color="green">
              Source: {formData.selectedSource || "Not selected"}
            </Tag>
            {formData.selectedSource === WorkOrderLineSource.NewWorkLine &&
              formData.workLevel && (
                <Tag color="cyan">Work Level: {formData.workLevel}</Tag>
              )}
            {formData.selectedSource === WorkOrderLineSource.NewWorkLine &&
              formData.budgetHeaderId && (
                <Tag color="purple">
                  Budget: {formData.budgetHeaderName || "Budget Selected"}
                </Tag>
              )}
            {formData.enableTax === "true" &&
              formData.taxApplicationLevel === TaxApplicationLevel.Both && (
                <Tag color="orange">Tax: {formData.taxApplicationLevel}</Tag>
              )}
            {formData.selectedSource === WorkOrderLineSource.NewWorkLine &&
              formData.workType && (
                <Tag color="pink">Work Type: {formData.workType}</Tag>
              )}
          </div>
          {formData.enableTax === "true" &&
            formData.taxApplicationLevel === TaxApplicationLevel.Both &&
            lineLevelTax === 0 &&
            workOrderLinesWatch.length > 0 && (
              <div className="mb-3 p-2 bg-warning rounded">
                <small className="text-dark">
                  <strong>⚠️ Notice:</strong> Tax application level is "Both"
                  but no line-level taxes detected. Please ensure tax slabs are
                  selected for individual work order lines.
                </small>
              </div>
            )}

          <div
            style={{
              width: window.innerWidth - 170 + "px",
            }}
          >
            <Table
              dataSource={workOrderLinesWatch}
              columns={getWorkOrderLinesColumns()}
              rowKey={(record) =>
                `${record.id}-${record.taxType}-${record.taxAmount}`
              }
              pagination={false}
              size="small"
              scroll={{ x: 1800 }}
              bordered
              className="custom-table"
              expandable={{
                expandedRowRender: (record: WorkOrderLine) => {
                  if (!record.stages || record.stages.length === 0) return null;

                  const totalPercentage = record.stages.reduce(
                    (sum, stage) => sum + (stage.splitPercentage || 0),
                    0
                  );
                  const totalAmount = record.stages.reduce(
                    (sum, stage) =>
                      sum + (record.rate * (stage.splitPercentage || 0)) / 100,
                    0
                  );

                  return (
                    <div
                      style={{
                        padding: "12px 16px",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <div
                        style={{
                          marginBottom: 8,
                          fontWeight: 600,
                          color: "#1890ff",
                        }}
                      >
                        <ScheduleOutlined style={{ marginRight: 8 }} />
                        Schedule Breakdown ({record.stages.length} stages)
                      </div>
                      <Table
                        dataSource={record.stages.map((stage, idx) => ({
                          ...stage,
                          key: idx,
                          amount:
                            (record.rate * (stage.splitPercentage || 0)) / 100,
                        }))}
                        columns={[
                          {
                            title: "Stage",
                            dataIndex: "stageTitle",
                            key: "stageTitle",
                            width: 300,
                          },
                          {
                            title: "Percentage",
                            dataIndex: "splitPercentage",
                            key: "splitPercentage",
                            width: 120,
                            render: (val: number) => (
                              <Tag color="blue">{val}%</Tag>
                            ),
                          },
                          {
                            title: "Amount",
                            dataIndex: "amount",
                            key: "amount",
                            width: 150,
                            render: (val: number) => (
                              <span style={{ fontWeight: 500 }}>
                                ₹
                                {val.toLocaleString("en-IN", {
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            ),
                          },
                        ]}
                        pagination={false}
                        size="small"
                        bordered
                        summary={() => (
                          <Table.Summary fixed>
                            <Table.Summary.Row
                              style={{ backgroundColor: "#e6f7ff" }}
                            >
                              <Table.Summary.Cell index={0}>
                                <strong>Total</strong>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={1}>
                                <Tag
                                  color={
                                    totalPercentage === 100 ? "green" : "orange"
                                  }
                                >
                                  {totalPercentage}%
                                </Tag>
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={2}>
                                <strong>
                                  ₹
                                  {totalAmount.toLocaleString("en-IN", {
                                    maximumFractionDigits: 2,
                                  })}
                                </strong>
                              </Table.Summary.Cell>
                            </Table.Summary.Row>
                          </Table.Summary>
                        )}
                      />
                    </div>
                  );
                },
                expandIcon: ({ expanded, onExpand, record }) => {
                  const hasStages = record.stages && record.stages.length > 0;
                  if (!hasStages) return null;

                  return expanded ? (
                    <DownOutlined
                      onClick={(e) => onExpand(record, e)}
                      style={{ color: "#722ed1", cursor: "pointer" }}
                    />
                  ) : (
                    <RightOutlined
                      onClick={(e) => onExpand(record, e)}
                      style={{ color: "#722ed1", cursor: "pointer" }}
                    />
                  );
                },
                rowExpandable: (record: WorkOrderLine) =>
                  !!(record.stages && record.stages.length > 0),
              }}
              locale={{ emptyText: "No work order lines configured" }}
            />
          </div>
          <div className="mt-3 p-3 bg-light rounded">
            <Row>
              <Col lg="8">
                {/* Subtotal */}
                <div className="d-flex justify-content-between">
                  <span>
                    <strong>Subtotal:</strong>
                  </span>
                  <strong>₹{subtotal.toLocaleString("en-IN")}</strong>
                </div>

                {/* GST Slab-wise Breakdown */}
                {formData.enableTax === "true" &&
                  Object.keys(gstSlabBreakdown).length > 0 && (
                    <>
                      <Divider className="my-2" style={{ margin: "8px 0" }} />
                      <div style={{ paddingLeft: "12px" }}>
                        {Object.entries(gstSlabBreakdown).map(
                          ([rate, amounts]) => (
                            <div key={rate}>
                              <div
                                className="d-flex justify-content-between"
                                style={{ fontSize: "13px", color: "#666" }}
                              >
                                <span>GST @ {rate}:</span>
                                <span></span>
                              </div>
                              {amounts.igst > 0 ? (
                                <div
                                  className="d-flex justify-content-between"
                                  style={{
                                    fontSize: "12px",
                                    paddingLeft: "16px",
                                  }}
                                >
                                  <span>IGST:</span>
                                  <span>₹{amounts.igst.toLocaleString("en-IN")}</span>
                                </div>
                              ) : (
                                <>
                                  <div
                                    className="d-flex justify-content-between"
                                    style={{
                                      fontSize: "12px",
                                      paddingLeft: "16px",
                                    }}
                                  >
                                    <span>CGST:</span>
                                    <span>
                                      ₹{amounts.cgst.toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                  <div
                                    className="d-flex justify-content-between"
                                    style={{
                                      fontSize: "12px",
                                      paddingLeft: "16px",
                                    }}
                                  >
                                    <span>SGST:</span>
                                    <span>
                                      ₹{amounts.sgst.toLocaleString("en-IN")}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    </>
                  )}

                {/* Header Level Tax (if any) */}
                {headerLevelTax > 0 && (
                  <div
                    className="d-flex justify-content-between"
                    style={{ paddingLeft: "12px", fontSize: "13px" }}
                  >
                    <span>{getTaxLabelFromId(formData.headerTaxType)}:</span>
                    <span>₹{headerLevelTax.toLocaleString("en-IN")}</span>
                  </div>
                )}

                {/* Gross Total - Only Subtotal + GST (no TDS/TCS) */}
                <Divider className="my-2" style={{ margin: "8px 0" }} />
                <div className="d-flex justify-content-between">
                  <span>
                    <strong>Gross Total:</strong>
                  </span>
                  <strong className="text-success" style={{ fontSize: "16px" }}>
                    ₹
                    {(
                      subtotal +
                      lineLevelTax +
                      headerLevelTax
                    ).toLocaleString("en-IN")}
                  </strong>
                </div>
                {/* {formData.enableTax === "true" && formData.taxApplicationLevel === TaxApplicationLevel.Both && (totalTaxAmount > 0 || tdsAmount > 0) && (
                  <div className="mt-2 p-2 bg-light rounded">
                    <small className="text-muted">
                      <strong>Calculation Breakdown (Tax Level: Both):</strong><br />
                      Subtotal: ₹{subtotal.toLocaleString("en-IN")}<br />
                      {lineLevelTax > 0 && (
                        <>+ Line Level Tax: ₹{lineLevelTax.toLocaleString("en-IN")}<br /></>
                      )}
                      {headerLevelTax > 0 && (
                        <>+ Header Level Tax: ₹{headerLevelTax.toLocaleString("en-IN")}<br /></>
                      )}
                      {tcsAmount > 0 && (
                        <>+ TCS: ₹{tcsAmount.toLocaleString("en-IN")}<br /></>
                      )}
                      {tdsAmount > 0 && (
                        <>- TDS: ₹{tdsAmount.toLocaleString("en-IN")}<br /></>
                      )}
                      = <strong>₹{grossTotal.toLocaleString("en-IN")}</strong>
                    </small>
                  </div>
                )} */}
              </Col>
            </Row>
          </div>
        </Card>

        {/* Section C: General Conditions Summary */}
        <Card title="📋 General Conditions Summary" className="mb-4">
          <div className="mb-3">
            <Tag color="blue">
              {generalConditionsWatch.length} condition(s) selected
            </Tag>
          </div>

          {generalConditionsWatch.length > 0 ? (
            <div>
              {generalConditionsWatch
                .sort((a: any, b: any) => a.order - b.order)
                .map((condition: any, index: any) => {
                  const hasContent =
                    condition.conditionContent &&
                    condition.conditionContent.trim() !== "";
                  const hasAttachments =
                    condition.attachments && condition.attachments.length > 0;

                  return (
                    <div key={condition.id} className="mb-4 p-3 border rounded">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="mb-0">
                          <FileTextOutlined className="me-2" />
                          {condition.order}. {condition.conditionName}
                        </h6>
                        <div>
                          {hasContent && <Tag color="green">Text</Tag>}
                          {hasAttachments && <Tag color="blue">Documents</Tag>}
                        </div>
                      </div>

                      {/* Text Content */}
                      {hasContent && (
                        <div className="mb-3">
                          <div className="p-2 bg-light rounded">
                            <small className="text-primary fw-medium">
                              Content:
                            </small>
                            <p
                              className="mb-0 mt-1 small"
                              style={{ whiteSpace: "pre-wrap" }}
                            >
                              {condition.conditionContent}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Attachments */}
                      {hasAttachments && (
                        <div className="mt-2">
                          <small className="text-primary fw-medium d-block mb-2">
                            <PaperClipOutlined className="me-1" />
                            Documents ({condition.attachments.length}):
                          </small>
                          <div className="row">
                            {condition.attachments.map(
                              (attachment: any, idx: any) => (
                                <div
                                  key={attachment.id}
                                  className="col-md-6 mb-2"
                                >
                                  <div className="border rounded p-2 d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                      <FileTextOutlined className="text-muted me-2" />
                                      <div>
                                        <div
                                          className="small fw-medium"
                                          title={attachment.fileName}
                                        >
                                          {attachment.fileName.length > 25
                                            ? `${attachment.fileName.substring(
                                              0,
                                              25
                                            )}...`
                                            : attachment.fileName}
                                        </div>
                                        <small className="text-muted">
                                          {(
                                            attachment.fileSize /
                                            1024 /
                                            1024
                                          ).toFixed(2)}
                                          MB
                                        </small>
                                      </div>
                                    </div>
                                    <div>
                                      <small className="text-muted">
                                        {dayjs(attachment.uploadDate).format(
                                          "DD/MM/YY"
                                        )}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {/* No content warning */}
                      {!hasContent && !hasAttachments && (
                        <div className="alert alert-warning small mb-0">
                          <strong>Note:</strong> This condition has no content
                          or documents attached.
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="text-center text-muted py-3">
              No general conditions selected
            </div>
          )}
        </Card>

        {/* Section D: Payment Conditions Summary */}
        <Card title="📋 Payment Conditions Summary" className="mb-4">
          <div className="mb-3">
            <Tag color="green">
              <DollarOutlined className="me-1" />
              Contract Value: ₹{grossTotal.toLocaleString("en-IN")}
            </Tag>
          </div>

          {paymentConditionsWatch &&
            (paymentConditionsWatch.mobilizationAdvance ||
              paymentConditionsWatch.retentionAmount ||
              paymentConditionsWatch.onHoldAmount ||
              paymentConditionsWatch.payOnWorkCompletion !== undefined) ? (
            <Descriptions bordered size="small" column={1}>
              {paymentConditionsWatch.mobilizationAdvance && (
                <Descriptions.Item label="Mobilization Advance">
                  {paymentConditionsWatch.mobilizationAdvance
                    .isPercentOfContract
                    ? `${paymentConditionsWatch.mobilizationAdvance
                      .percentageOfContract || 0
                    }% of contract value`
                    : `₹${(
                      paymentConditionsWatch.mobilizationAdvance
                        .advanceValue || 0
                    ).toLocaleString("en-IN")}`}
                  {paymentConditionsWatch.mobilizationAdvance.interestCharged &&
                    ` (Interest: ${paymentConditionsWatch.mobilizationAdvance.interestRate ||
                    0
                    }%)`}
                  <br />
                  <small className="text-muted">
                    Recovery Type:{" "}
                    {paymentConditionsWatch.mobilizationAdvance.recoveryType ||
                      "INSTALLMENTS"}
                    {paymentConditionsWatch.mobilizationAdvance
                      .recoveryPercentRate &&
                      paymentConditionsWatch.mobilizationAdvance
                        .recoveryPercentRate > 0 && (
                        <>
                          <br />
                          Recovery:{" "}
                          {
                            paymentConditionsWatch.mobilizationAdvance
                              .recoveryPercentRate
                          }
                          % per RA Bill
                          {paymentConditionsWatch.mobilizationAdvance
                            .recoveryCapPercent &&
                            paymentConditionsWatch.mobilizationAdvance
                              .recoveryCapPercent > 0 && (
                              <>
                                {" "}upto{" "}
                                {
                                  paymentConditionsWatch.mobilizationAdvance
                                    .recoveryCapPercent
                                }
                                % of Work Order
                              </>
                            )}
                        </>
                      )}
                  </small>
                </Descriptions.Item>
              )}

              {paymentConditionsWatch.retentionAmount && (
                <Descriptions.Item label="Retention Amount">
                  {paymentConditionsWatch.retentionAmount.isPercentOfRABill
                    ? `${paymentConditionsWatch.retentionAmount
                      .percentageOfRABill || 0
                    }% of RA Bill`
                    : `₹${(
                      paymentConditionsWatch.retentionAmount.retentionValue ||
                      0
                    ).toLocaleString("en-IN")}`}
                  <br />
                  <small className="text-muted">
                    DLP: {paymentConditionsWatch.retentionAmount.dlpAmount || 1}{" "}
                    {paymentConditionsWatch.retentionAmount.dlpUnit || "years"}
                    <br />
                    Release Date:{" "}
                    {paymentConditionsWatch.retentionAmount.releaseDate
                      ? dayjs(
                        paymentConditionsWatch.retentionAmount.releaseDate
                      ).format("DD-MMM-YYYY")
                      : "Not set"}
                  </small>
                </Descriptions.Item>
              )}

              {paymentConditionsWatch.onHoldAmount && (
                <Descriptions.Item label="On Hold Amount">
                  {paymentConditionsWatch.onHoldAmount.isPercentOfContract
                    ? `${paymentConditionsWatch.onHoldAmount
                      .percentageOfContract || 0
                    }% of RABill`
                    : `₹${(
                      paymentConditionsWatch.onHoldAmount.holdAmountValue || 0
                    ).toLocaleString("en-IN")}`}
                  <br />
                  <small className="text-muted">
                    DLP: {paymentConditionsWatch.onHoldAmount.dlpAmount || 1}{" "}
                    {paymentConditionsWatch.onHoldAmount.dlpUnit || "years"}
                    <br />
                    Release Date:{" "}
                    {paymentConditionsWatch.onHoldAmount.releaseDate
                      ? dayjs(
                        paymentConditionsWatch.onHoldAmount.releaseDate
                      ).format("DD-MMM-YYYY")
                      : "Not set"}
                  </small>
                </Descriptions.Item>
              )}

              {paymentConditionsWatch.payOnWorkCompletion !== undefined && (
                <Descriptions.Item label="Pay on Work Completion">
                  <Tag
                    color={
                      paymentConditionsWatch.payOnWorkCompletion
                        ? "orange"
                        : "green"
                    }
                  >
                    {paymentConditionsWatch.payOnWorkCompletion ? "Yes" : "No"}
                  </Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          ) : (
            <div className="text-center text-muted py-3">
              No payment conditions configured
            </div>
          )}
        </Card>

        <div className="actions clearfix">
          <ul>
            <li>
              <Space>
                {formData.type === "edit" ? (
                  <>
                    {/* Edit mode with NON-DRAFT status: Show only Update button */}
                    {originalData && originalData.status !== "Draft" ? (
                      <Button
                        color="primary"
                        type="button"
                        onClick={handleSubmit((data) =>
                          submitForm(data, false, false)
                        )}
                        loading={submitting}
                        disabled={workOrderLinesWatch.length === 0}
                      >
                        Update Work Order
                      </Button>
                    ) : (
                      /* Edit mode with DRAFT status: Show Submit + Save as Draft buttons */
                      <>
                        <Button
                          color="secondary"
                          type="button"
                          onClick={handleSubmit((data) =>
                            submitForm(data, true, false)
                          )}
                          loading={submitting}
                          disabled={workOrderLinesWatch.length === 0}
                        >
                          Save as Draft
                        </Button>
                        <Button
                          color="primary"
                          type="button"
                          onClick={handleSubmit((data) =>
                            submitForm(data, false, true)
                          )}
                          loading={submitting}
                          disabled={workOrderLinesWatch.length === 0}
                        >
                          Submit Work Order
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      color="secondary"
                      type="button"
                      onClick={handleSubmit((data) => submitForm(data, true))}
                      loading={submitting}
                      disabled={workOrderLinesWatch.length === 0}
                    >
                      Save as Draft
                    </Button>
                    <Button
                      color="primary"
                      type="button"
                      onClick={handleSubmit((data) => submitForm(data, false))}
                      loading={submitting}
                      disabled={workOrderLinesWatch.length === 0}
                    >
                      {formData.approvalRequired === "true"
                        ? "Submit for Approval"
                        : "Submit Work Order"}
                    </Button>
                  </>
                )}
              </Space>
            </li>
            <li>
              <Button
                color="primary"
                className="btn-dim btn btn-secondary"
                onClick={handlePrevious}
              >
                Previous
              </Button>
            </li>
          </ul>
        </div>
      </form>
    </>
  );
};

export default WorkOrderPreview;
